import { firestore, storage, app } from "@redux/firebase";
import { doc, setDoc, getDoc, updateDoc, deleteDoc, collection, query, orderBy, where, getDocs, increment, addDoc, serverTimestamp, getFirestore, limit, documentId, startAfter, onSnapshot, arrayUnion } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { ref, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";
import i18next from 'i18next';
import { examAudioUrlMap } from "@pages/chat/chatCard/chatCardForm/audioRecorder/hook/useAudioRecorder";
import { leggiDocumentiPerId } from "@lib/leggiDocumentiPerId";

// Helper per accedere alle traduzioni nei file non-React
const t = (key, options) => i18next.t(key, { ns: 'common', ...options });

/**
 * Helper per recuperare il testo di un documento, gestendo sia Firestore che Storage
 * @param {Object} documentData - I dati del documento da Firestore
 * @returns {Promise<string>} - Il testo del documento
 */
export const fetchDocumentText = async (documentData) => {
  if (!documentData) return null;

  // Se il testo è già presente in Firestore, usalo
  if (documentData.text && !documentData.isStoredInStorage) {
    return documentData.text;
  }

  // Se il testo è salvato su Storage, recuperalo
  if (documentData.isStoredInStorage && documentData.textStorageRef) {
    try {
      console.log(`📥 Recupero testo da Storage: ${documentData.textStorageRef}`);
      const textRef = ref(storage, documentData.textStorageRef);
      const downloadUrl = await getDownloadURL(textRef);
      const response = await fetch(downloadUrl);
      const text = await response.text();
      console.log(`✅ Testo recuperato da Storage (${text.length} caratteri)`);
      return text;
    } catch (storageError) {
      console.error('❌ Errore nel recupero del testo da Storage:', storageError);
      return null;
    }
  }

  // Fallback: testo non disponibile
  return documentData.text || null;
};

/**
 * Mappatura dei documenti della SUBCOLLECTION questions/{documentId}/qa/ nella
 * forma che si aspetta chi legge le domande (metadata.isFlashcard e compagnia).
 *
 * Sta qui fuori perche' la stessa lettura serve in due modi: una tantum
 * (getQuestions) e dal vivo (onSnapshot, hub Ripasso). Duplicarla significa
 * che il giorno in cui cambia un tipo, uno dei due lo sa e l'altro no.
 */
export const mapQaDocs = (documentId, qaSnapshot) =>
  (qaSnapshot?.docs || []).map((qaDoc) => ({
    id: qaDoc.id,
    data: () => {
      const data = qaDoc.data();
      return {
        ...data,
        documentID: documentId,
        contentUUID: data.contentUUID,
        metadata: {
          isExamQuestion: data.type === 'exam',
          isQuizQuestion: data.type === 'quiz',
          isFlashcard: data.type === 'flashcard',
          ...(data.metadata || {})
        }
      };
    }
  }));

// get document's questions
export const getQuestions = async (documentId, contentUUID) => {
  try {
    let foundQuestions = false;

    // 🆕 PRIORITÀ 1: Prova la SUBCOLLECTION questions/{documentId}/qa/
    try {
      const qaCollectionRef = collection(firestore, "questions", documentId, "qa");
      const qaSnapshot = await getDocs(qaCollectionRef);

      if (!qaSnapshot.empty) {
        console.log(`✅ getQuestions - Trovate ${qaSnapshot.size} domande nella SUBCOLLECTION questions/${documentId}/qa/`);
        foundQuestions = true;

        return { docs: mapQaDocs(documentId, qaSnapshot) };
      }
    } catch (subcollectionError) {
      console.log('ℹ️ getQuestions - SUBCOLLECTION non trovata, provo formati legacy');
    }

    // Caso speciale: verifica se il documentId stesso è il documento con le domande (legacy)
    try {
      const mainDocRef = doc(firestore, "documents", documentId);
      const mainDocSnap = await getDoc(mainDocRef);

      if (mainDocSnap.exists()) {
        const mainDocData = mainDocSnap.data();

        // 🚨 FIX: Ricostruisci la qaMap dai campi flat tipo "qaMap.qa_0_xxx"
        let qaMap = mainDocData.qaMap || {};

        // Se qaMap è vuoto o è un oggetto vuoto, cerca i campi flat
        if (Object.keys(qaMap).length === 0) {
          // Cerca tutti i campi che iniziano con "qaMap."
          const qaMapFields = Object.keys(mainDocData).filter(key => key.startsWith('qaMap.'));
          
          if (qaMapFields.length > 0) {
            qaMap = {};
            qaMapFields.forEach(flatKey => {
              // Estrai la chiave reale: "qaMap.qa_0_xxx" -> "qa_0_xxx"
              const realKey = flatKey.replace('qaMap.', '');
              qaMap[realKey] = mainDocData[flatKey];
            });
            console.log('✅ getQuestions - qaMap ricostruita da documents con', Object.keys(qaMap).length, 'elementi');
          }
        }
        
        // Se il documento stesso contiene qaMap (ora ricostruita)
        if (qaMap && Object.keys(qaMap).length > 0) {
          foundQuestions = true;
          
          // Trasforma le domande nel formato atteso
          return {
            docs: Object.entries(qaMap).map(([id, data]) => ({
              id,
              data: () => ({
                ...data,
                documentID: documentId,
                contentUUID: mainDocData.contentUUID,
                metadata: { 
                  isExamQuestion: data.type === 'exam',
                  isQuizQuestion: data.type === 'quiz',
                  isFlashcard: data.type === 'flashcard',
                  ...(data.metadata || {})
                }
              })
            }))
          };
        }
      }
    } catch (mainDocError) {
      console.error("Errore durante il controllo del documento principale:", mainDocError);
      // Continua con gli altri tentativi
    }
    
    // Primo tentativo: nuovo formato con documento unico e mappa QA
    const newFormatDoc = await getDoc(doc(firestore, "questions", documentId));
    
    if (newFormatDoc.exists()) {
      const docData = newFormatDoc.data();
      
      // 🚨 FIX: Ricostruisci la qaMap dai campi flat tipo "qaMap.qa_0_xxx"
      let qaMap = docData.qaMap || {};
      
      // Se qaMap è vuoto, cerca i campi flat
      if (Object.keys(qaMap).length === 0) {
        // Cerca tutti i campi che iniziano con "qaMap."
        const qaMapFields = Object.keys(docData).filter(key => key.startsWith('qaMap.'));
        
        if (qaMapFields.length > 0) {
          qaMap = {};
          qaMapFields.forEach(flatKey => {
            // Estrai la chiave reale: "qaMap.qa_0_xxx" -> "qa_0_xxx"
            const realKey = flatKey.replace('qaMap.', '');
            qaMap[realKey] = docData[flatKey];
          });
          console.log('✅ getQuestions - qaMap ricostruita da questions con', Object.keys(qaMap).length, 'elementi');
        }
      }
      
      // Verifica se il documento ha una mappa QA (ora ricostruita)
      if (qaMap && Object.keys(qaMap).length > 0) {
        foundQuestions = true;
        
        return {
          docs: Object.entries(qaMap).map(([id, data]) => ({
            id,
            data: () => ({
              ...data,
              documentID: documentId,
              contentUUID: docData.contentUUID,
              // Manteniamo i metadata per compatibilità con codice legacy
              metadata: { 
                isExamQuestion: data.type === 'exam',
                isQuizQuestion: data.type === 'quiz',
                isFlashcard: data.type === 'flashcard',
                ...(data.metadata || {})  // Preserva i metadata originali
              }
            })
          }))
        };
      }
    }

    // Fallback al formato legacy (documenti separati)
    if (!foundQuestions) {
      const questionsQuery = query(
        collection(firestore, "questions"),
        where("documentID", "==", documentId),
        /* orderBy("uploadDate", "desc") */
      );

      const legacyResults = await getDocs(questionsQuery);
      
      // Se abbiamo trovato risultati, li restituiamo direttamente
      if (!legacyResults.empty) {
        foundQuestions = true;
        
        if (legacyResults.size > 0) {
          const firstDoc = legacyResults.docs[0];
        }
        return legacyResults;
      }
    }
    
    // Terzo tentativo: cerca utilizzando contentUUID invece di documentID
    // Rimuoviamo l'orderBy per evitare errori di indice
    if (!foundQuestions) {
      try {
        if (!contentUUID) {
          return { docs: [] }; // Restituisci un risultato vuoto compatibile
        }
        
        const contentUUIDQuery = query(
          collection(firestore, "questions"),
          where("contentUUID", "==", contentUUID)
        );
        
        const contentResults = await getDocs(contentUUIDQuery);
        
        if (!contentResults.empty && contentResults.size > 0) {
          const firstDoc = contentResults.docs[0];
          foundQuestions = true;
        }
        
        return contentResults;
      } catch (contentQueryError) {
        console.error("Errore nella query contentUUID:", contentQueryError);
        // Restituisci un oggetto vuoto compatibile con l'interfaccia
        return { docs: [] };
      }
    }
    
    // Se arriviamo qui, non abbiamo trovato nulla
    return { docs: [] };
    
  } catch (error) {
    console.error("Errore nel recupero delle domande:", error);
    return { docs: [] }; // Resistenza agli errori
  }
};

// get question
export const getQuestion = async (documentId, questionId, contentUUID) => {
  try {
    console.log('[getQuestion] Input:', { documentId, questionId, contentUUID });

    // 🆕 PRIORITÀ 1: Cerca nella SUBCOLLECTION questions/{documentId}/qa/{questionId}
    if (questionId && documentId) {
      try {
        const qaDocRef = doc(firestore, "questions", documentId, "qa", questionId);
        const qaDocSnap = await getDoc(qaDocRef);

        if (qaDocSnap.exists()) {
          console.log('[getQuestion] ✅ Domanda trovata nella SUBCOLLECTION');
          return { id: questionId, ...qaDocSnap.data() };
        }
      } catch (subcollectionError) {
        console.log('[getQuestion] ℹ️ Domanda non trovata in SUBCOLLECTION, provo formati legacy');
      }
    }

    // Legacy: Nuovo formato con qaMap nel documento parent
    if (questionId && documentId) {
      console.log('[getQuestion] Tentativo: Formato legacy con documentId + questionId (qaMap)');
      const parentDocRef = doc(firestore, "questions", documentId);
      const parentDocSnap = await getDoc(parentDocRef);

      if (parentDocSnap.exists()) {
        const qaMap = parentDocSnap.data().qaMap || {};
        console.log('[getQuestion] Documento trovato, qaMap keys:', Object.keys(qaMap).slice(0, 5));

        if (qaMap[questionId]) {
          console.log('[getQuestion] ✅ Domanda trovata in qaMap');
          return { id: questionId, ...qaMap[questionId] };
        } else {
          console.log('[getQuestion] ❌ questionId non trovato in qaMap');
        }
      } else {
        console.log('[getQuestion] ❌ Documento non esiste:', documentId);
      }
    }

    // Formato legacy con 1 parametro - solo se documentId non è null
    if (documentId) {
      console.log('[getQuestion] Tentativo: Formato legacy con solo documentId');
      const legacyDocRef = doc(firestore, "questions", documentId);
      const legacyDocSnap = await getDoc(legacyDocRef);

      if (legacyDocSnap.exists()) {
        console.log('[getQuestion] ✅ Documento legacy trovato');
        return { id: documentId, ...legacyDocSnap.data() };
      } else {
        console.log('[getQuestion] ❌ Documento legacy non esiste');
      }
    }

    // Fallback aggiuntivo: cerca tutte le domande con contentUUID e trova quella con questionId
    if (contentUUID && questionId) {
      console.log('[getQuestion] Tentativo: Fallback con contentUUID query in questions collection');
      // STU-480: `questions` non ha un proprietario, quindi una query per
      // contentUUID le regole la negano (poteva restituire le domande di
      // chiunque avesse lo stesso contentUUID). Negata = nessun risultato, e
      // si passa ai documenti propri qui sotto.
      const contentUUIDQuery = query(
        collection(firestore, "questions"),
        where("contentUUID", "==", contentUUID)
      );

      let questionsList = { empty: true, size: 0, docs: [] };
      try {
        questionsList = await getDocs(contentUUIDQuery);
      } catch (questionsQueryError) {
        console.log('[getQuestion] Query questions per contentUUID non permessa, provo i documenti propri');
      }

      // 🎯 NUOVO: Cerca anche nella collection documents, fra i PROPRI
      console.log('[getQuestion] Tentativo: Fallback cerca anche in documents collection');
      const ownerId = getAuth().currentUser?.uid || JSON.parse(localStorage.getItem("userId") || "null");
      const documentsQuery = query(
        collection(firestore, "documents"),
        where("contentUUID", "==", contentUUID),
        where("uploadedBy", "==", ownerId || "-")
      );
      const documentsList = await getDocs(documentsQuery);
      console.log('[getQuestion] Query results - questions:', questionsList.size, 'documents:', documentsList.size);

      // 🎯 PRIORITÀ 1: Cerca prima nella collection documents (dove le domande sono salvate correttamente)
      if (!documentsList.empty) {
        console.log('[getQuestion] ✅ Trovato documento in documents collection');
        for (const docSnap of documentsList.docs) {
          const docData = docSnap.data();
          console.log(`[getQuestion] Documento ${docSnap.id} - qaMap keys:`, docData.qaMap ? Object.keys(docData.qaMap).length : 0);

          if (docData.qaMap && docData.qaMap[questionId]) {
            console.log('[getQuestion] ✅ Domanda trovata in documents/qaMap:', questionId);
            return { id: questionId, ...docData.qaMap[questionId] };
          }

          // Fallback: prendi la prima domanda disponibile
          if (docData.qaMap && typeof docData.qaMap === 'object') {
            const qaMapKeys = Object.keys(docData.qaMap);
            if (qaMapKeys.length > 0) {
              const firstKey = qaMapKeys[0];
              const firstQuestion = docData.qaMap[firstKey];
              if (firstQuestion && firstQuestion.question && firstQuestion.answer) {
                console.log('[getQuestion] ✅ Usando prima domanda da documents/qaMap:', firstKey);
                return {
                  id: questionId || firstKey,
                  question: firstQuestion.question,
                  answer: firstQuestion.answer,
                  type: firstQuestion.type || 'exam'
                };
              }
            }
          }
        }
      }

      if (!questionsList.empty) {
        // Log dei documenti trovati
        questionsList.docs.forEach((doc, idx) => {
          const data = doc.data();
          console.log(`[getQuestion] Doc ${idx}:`, {
            id: doc.id,
            hasQaMap: !!data.qaMap,
            qaMapKeys: data.qaMap ? Object.keys(data.qaMap).slice(0, 5) : [],
            contentUUID: data.contentUUID
          });
        });

        // Prima verifichiamo se c'è una corrispondenza diretta dell'ID
        let foundQuestion = questionsList.docs.find(doc => doc.id === questionId);

        if (foundQuestion) {
          console.log('[getQuestion] ✅ Corrispondenza diretta ID trovata');
          return { id: questionId, ...foundQuestion.data() };
        } else {
          console.log('[getQuestion] ❌ Nessuna corrispondenza diretta ID');
        }

        // Se non troviamo una corrispondenza diretta e le domande hanno campi qaMap
        for (const doc of questionsList.docs) {
          const data = doc.data();
          if (data.qaMap && data.qaMap[questionId]) {
            console.log('[getQuestion] ✅ Domanda trovata in qaMap del documento:', doc.id);
            return { id: questionId, ...data.qaMap[questionId] };
          }
        }

        console.log('[getQuestion] ❌ questionId non trovato in nessun qaMap');

        // 🎯 FALLBACK MIGLIORATO: Se qaMap esiste ma è vuoto o non contiene questionId,
        // cerca QUALSIASI domanda valida nel qaMap per permettere il feedback
        console.log('[getQuestion] Tentativo: Fallback cerca prima domanda disponibile in qaMap');
        for (const doc of questionsList.docs) {
          const data = doc.data();

          // Debug completo della struttura qaMap
          console.log(`[getQuestion] DEBUG qaMap per documento ${doc.id}:`, {
            hasQaMap: !!data.qaMap,
            qaMapType: typeof data.qaMap,
            qaMapKeys: data.qaMap ? Object.keys(data.qaMap) : [],
            qaMapSample: data.qaMap ? JSON.stringify(data.qaMap).substring(0, 200) : 'N/A'
          });

          if (data.qaMap && typeof data.qaMap === 'object') {
            const qaMapKeys = Object.keys(data.qaMap);
            console.log(`[getQuestion] qaMap trovato con ${qaMapKeys.length} domande nel documento ${doc.id}`);

            if (qaMapKeys.length > 0) {
              // Prendi la prima domanda disponibile
              const firstKey = qaMapKeys[0];
              const firstQuestion = data.qaMap[firstKey];

              if (firstQuestion && firstQuestion.question && firstQuestion.answer) {
                console.log('[getQuestion] ✅ Usando prima domanda disponibile in qaMap:', firstKey);
                return {
                  id: questionId || firstKey, // Usa il questionId richiesto se fornito
                  question: firstQuestion.question,
                  answer: firstQuestion.answer,
                  type: firstQuestion.type || 'exam'
                };
              }
            }
          }
        }
      } else {
        console.log('[getQuestion] ❌ Nessun documento trovato con contentUUID:', contentUUID);
      }
    }

    console.log('[getQuestion] ❌ Tutti i tentativi falliti, ritorno oggetto vuoto');
    return {};
  } catch (error) {
    console.error("[getQuestion] Errore nel recupero della domanda:", error);
    throw error;
  }
};

// get user's documents with pagination support
/**
 * @param {object} [options]
 * @param {boolean} [options.skipStorageText=false] - non scaricare il testo
 *   da Storage (liste: la sidebar della shell chiede fino a 200 documenti per
 *   mostrarne titolo e tipo; il testo lo ricarica chi apre il documento con
 *   getDocumentById). Default false: i chiamanti esistenti non cambiano.
 */
export const getUserDocuments = async (limitCount = 5, startAfterDoc = null, options = {}) => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    
    // Disabilitato l'uso della cache, carica sempre dal database
    
    // Query ottimizzata con limite e paginazione
    let documentsQuery;
    
    if (startAfterDoc) {
      // Query per pagine successive
      documentsQuery = query(
        collection(firestore, "documents"),
        where("uploadedBy", "==", userId),
        orderBy("uploadDate", "desc"),
        startAfter(startAfterDoc),  // Inizia dopo l'ultimo documento
        limit(limitCount)
      );
    } else {
      // Query per la prima pagina
      documentsQuery = query(
        collection(firestore, "documents"),
        where("uploadedBy", "==", userId),
        orderBy("uploadDate", "desc"),  // Ordina per data più recente
        limit(limitCount)  // ✅ LIMITA A 5 DOCUMENTI di default!
      );
    }

    const querySnapshot = await getDocs(documentsQuery);
    
    // Ottieni il documento demo (verrà mostrato SOLO se l'utente non ha documenti)
    let demoDocument = null;
    try {
      const demoDocRef = doc(firestore, "documents", "cOwaT5DWy3HLmkAn6uAt");
      const demoDocSnap = await getDoc(demoDocRef);
      
      if (demoDocSnap.exists()) {
        demoDocument = {
          id: "cOwaT5DWy3HLmkAn6uAt",
          ...demoDocSnap.data(),
          // Imposta sempre il titolo a "Demo" indipendentemente da quello nel database
          title: "Demo",
          // Aggiungi tag intelligenti e educativi per il documento demo
          tags: [
            "Letteratura italiana", 
            "Dante Alighieri", 
            "Divina Commedia", 
            "Poesia epica", 
            "Medioevo",
            "Classici"
          ]
        };
      } else {
        console.log("Documento demo non trovato nel database");
      }
    } catch (error) {
      console.error("Errore nel caricamento del documento demo:", error);
    }
    
    // Filtriamo i documenti (già ordinati dalla query)
    let documents = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      // Filtriamo i documenti rimossi
      .filter(doc => !doc.isRemoved)
      // Sostituisci il titolo e aggiungi tag al documento demo se presente
      .map(doc => {
        if (doc.id === "cOwaT5DWy3HLmkAn6uAt") {
          return {
            ...doc,
            title: "Demo",
            // Aggiungi tag intelligenti e educativi per il documento demo
            tags: [
              "Letteratura italiana",
              "Dante Alighieri",
              "Divina Commedia",
              "Poesia epica",
              "Medioevo",
              "Classici"
            ]
          };
        }
        return doc;
      });

    // 🔧 FIX: Recupera il testo dallo Storage per i documenti che lo hanno salvato lì
    // Questo è necessario per mostrare il testo nel componente FilteredDocument
    const documentsWithStorageText = options.skipStorageText
      ? []
      : documents.filter(doc => doc.isStoredInStorage && doc.textStorageRef);
    if (documentsWithStorageText.length > 0) {
      console.log(`📥 Recupero testo da Storage per ${documentsWithStorageText.length} documenti...`);

      // Recupera il testo in parallelo per tutti i documenti che lo hanno in Storage
      const textPromises = documentsWithStorageText.map(async (doc) => {
        try {
          const text = await fetchDocumentText(doc);
          return { id: doc.id, text };
        } catch (error) {
          console.error(`❌ Errore recupero testo per documento ${doc.id}:`, error);
          return { id: doc.id, text: null };
        }
      });

      const textsFromStorage = await Promise.all(textPromises);

      // Aggiorna i documenti con il testo recuperato
      documents = documents.map(doc => {
        const storageText = textsFromStorage.find(t => t.id === doc.id);
        if (storageText && storageText.text) {
          console.log(`✅ Testo recuperato da Storage per documento: ${doc.title}`);
          return { ...doc, text: storageText.text };
        }
        return doc;
      });
    }
    
    // LOGICA INTELLIGENTE PER IL DOCUMENTO DEMO:
    // 1. Solo nella prima pagina (non c'è startAfterDoc)
    // 2. Non deve essere già presente nella lista
    if (demoDocument && !startAfterDoc && !documents.some(doc => doc.id === "cOwaT5DWy3HLmkAn6uAt")) {
      if (documents.length === 0) {
        // Se l'utente non ha documenti → Demo in cima per aiutarlo a iniziare
        documents.unshift(demoDocument);
      } else {
        // Se l'utente ha documenti → Demo nella posizione cronologica corretta
        // Il documento demo dovrebbe avere una data di upload, se non ce l'ha usiamo una data molto vecchia
        if (!demoDocument.uploadDate) {
          // Assegna una data molto vecchia per posizionarlo in fondo
          demoDocument.uploadDate = new Date('2020-01-01');
        }

        // Trova la posizione corretta in base alla data di upload (ordine decrescente)
        let insertIndex = documents.length; // Default: in fondo
        for (let i = 0; i < documents.length; i++) {
          const docDate = documents[i].uploadDate?.toDate ? documents[i].uploadDate.toDate() : documents[i].uploadDate;
          const demoDate = demoDocument.uploadDate?.toDate ? demoDocument.uploadDate.toDate() : demoDocument.uploadDate;

          if (demoDate && docDate && demoDate > docDate) {
            insertIndex = i;
            break;
          }
        }

        documents.splice(insertIndex, 0, demoDocument);
      }
    }

    // LOGICA PER DOCUMENTI CONDIVISI DAL PROFESSORE:
    // Usa subcollection: students/{userId}/sharedDocuments
    // Solo nella prima pagina (non c'è startAfterDoc)
    if (!startAfterDoc) {
      try {
        // Ottieni i documenti dalla subcollection sharedDocuments
        const sharedDocsRef = collection(firestore, 'students', userId, 'sharedDocuments');
        const sharedDocsSnapshot = await getDocs(sharedDocsRef);

        if (!sharedDocsSnapshot.empty) {
          console.log(`📚 Caricamento ${sharedDocsSnapshot.size} documenti condivisi dal professore...`);

          // Fetch ogni documento condiviso
          for (const sharedDocSnap of sharedDocsSnapshot.docs) {
            const sharedDocId = sharedDocSnap.id;
            const sharedData = sharedDocSnap.data();

            // Salta se il documento è già nella lista (non dovrebbe succedere, ma per sicurezza)
            if (documents.some(d => d.id === sharedDocId)) {
              continue;
            }

            try {
              const sharedDocRef = doc(firestore, 'documents', sharedDocId);
              const docSnap = await getDoc(sharedDocRef);

              if (docSnap.exists() && !docSnap.data().isRemoved) {
                const sharedDocument = {
                  id: sharedDocId,
                  ...docSnap.data(),
                  isFromProfessor: true,
                  readOnly: true,
                  sharedAt: sharedData.sharedAt,
                  teacherName: sharedData.teacherName
                };

                // Aggiungi in fondo alla lista (i documenti condivisi appaiono dopo quelli propri)
                documents.push(sharedDocument);
                console.log(`✅ Documento condiviso caricato: ${sharedDocument.title}`);
              }
            } catch (docError) {
              console.error(`❌ Errore nel caricamento del documento condiviso ${sharedDocId}:`, docError);
            }
          }
        }
      } catch (sharedError) {
        console.error('❌ Errore nel caricamento dei documenti condivisi:', sharedError);
      }
    }

    // Ottieni l'ultimo documento per la paginazione
    const lastDocument = querySnapshot.docs.length > 0 
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : null;
    
    // Prepara il risultato con info paginazione
    const result = {
      docs: documents,
      empty: documents.length === 0,
      size: documents.length,
      lastDocument: lastDocument,
      hasMore: querySnapshot.docs.length === limitCount  // Se ha caricato il limite, probabilmente ce ne sono altri
    };
    
    return result;
  } catch (error) {
    console.error("Errore nel recupero dei documenti:", error);
    throw new Error("Errore nel recupero dei documenti dell'utente");
  }
};

// get user's documents not reviewed
export const getUserDocumentsNotReviewed = async () => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    const examQuery = query(collection(firestore, "exam"));
    const documentsQuery = query(collection(firestore, "documents"), where("uploadedBy", "==", userId));
    const examDocsPromise = getDocs(examQuery);
    const documentsDocsPromise = getDocs(documentsQuery);

    // Wait for both queries to complete
    const [examDocs, documentsDocs] = await Promise.all([examDocsPromise, documentsDocsPromise]);

    // Extract data from document snapshots
    const examData = examDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const documentsData = documentsDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return documentsData.filter(document => document.id !== examData.map(exam => exam.documentID));
  } catch (error) {
    throw error;
  }
};

// get user's profile
export const getUserProfile = async userId => {
  try {
    // Check cache first
    const cachedData = localStorage.getItem(`user_profile_cache_${userId}`);
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        
        // Use cache if it's less than 5 minutes old
        if (cacheAge < 5 * 60 * 1000) {
          return data;
        }
      } catch (cacheError) {
        console.error("Errore nel parsing della cache profilo:", cacheError);
      }
    }
    
    const userRef = doc(firestore, "students", userId);
    const userSnap = await getDoc(userRef);
    const rawUserData = userSnap.data();

    // Entitlement EFFETTIVO anche qui (coerenza con getUser): pages/plans legge
    // `plan`/`selectedPlan` da getUserProfile e ne deriva il trigger
    // OffboardingFlow. Senza la promozione, un utente in trial vedrebbe un piano
    // non promosso da questa lettura e il trigger potrebbe sfasarsi. Additivo:
    // promuove SOLO se c'è una sub live (e non in pausa), mai un downgrade.
    const userData = await applyEffectivePremium(userId, rawUserData);

    // Save to cache with timestamp
    try {
      localStorage.setItem(`user_profile_cache_${userId}`, JSON.stringify({
        data: userData,
        timestamp: Date.now()
      }));
    } catch (storageError) {
      console.error("Errore nel salvataggio della cache profilo:", storageError);
    }

    return userData;
  } catch (error) {
    toast.error(t('toast.error.profileFetchError'));
    throw error;
  }
};

// update resource
export const updateResource = async (collectionName, resourceId, updateData) => {
  try {
    const resourceRef = doc(firestore, collectionName, resourceId);
    await updateDoc(resourceRef, { ...updateData });
  } catch (error) {
    toast.error(t('toast.error.resourceUpdateError'));
    throw error;
  }
};

// save exam
export const saveQAExam = async (examID, messages, reviewMode) => {
  try {
    const examSessionRef = doc(firestore, "examSessions", examID);
    
    // 🚨 FIX: Verifica se esiste già un examSession con questo examID
    const existingSession = await getDoc(examSessionRef);
    const existingData = existingSession.exists() ? existingSession.data() : null;
    
    console.log(`[saveQAExam] ExamID: ${examID} - Sessione ${existingData ? 'esistente trovata' : 'nuova'}`);
    console.log(`[saveQAExam] ReviewMode: ${reviewMode}, Messages count: ${messages?.length || 0}`);
    
    // 🚨 DEBUG: Log completo dei messaggi in arrivo
    if (process.env.NODE_ENV === 'development') {
      console.log(`[saveQAExam] 📋 TUTTI I MESSAGGI IN ARRIVO:`, messages);
      if (messages && messages.length > 0) {
        messages.forEach((msg, index) => {
          console.log(`[saveQAExam] 📝 Messaggio ${index}:`, msg);
        });
      }
      
      if (reviewMode !== false && messages && messages.length > 0 && messages[0] && Array.isArray(messages[0]) && messages[0].length > 1) {
        const [key, qaPairs] = messages[0];
        console.log(`[saveQAExam] Review mode - Struttura qaPairs:`, qaPairs);
        if (qaPairs && qaPairs.length > 1) {
          console.log(`[saveQAExam] userAnswer oggetto:`, qaPairs[1]);
        }
      }
    }
    
    let chatMessages;

    if (reviewMode === false) {
      // Formato per modalità non di revisione (interrogazione)
      // Validazione: assicurati che messages sia un array valido
      if (!Array.isArray(messages) || messages.length === 0) {
        console.error(`[saveQAExam] Messages non valido per modalità interrogazione:`, messages);
        chatMessages = [];
      } else {
        chatMessages = messages.map((message, arrayIndex) => {
          // 🚨 FIX: Usa l'indice reale della domanda dal campo message.index
          const questionIndex = message.index !== undefined ? message.index : arrayIndex;
          const questionKey = `q${questionIndex}`;
          
          // Debug: logga la generazione della chiave
          if (process.env.NODE_ENV === 'development') {
            console.log('[saveQAExam] Usando chiave stabile:', questionKey, 'per examID=', examID, 'questionIndex=', questionIndex, 'arrayIndex=', arrayIndex);
            console.log('[saveQAExam] Message data:', { 
              question: (message.question && typeof message.question === 'string') ? message.question.substring(0, 50) : 'N/A', 
              answer: (message.answer && typeof message.answer === 'string') ? message.answer.substring(0, 50) : (message.answer ? String(message.answer).substring(0, 50) : 'N/A'), 
              index: message.index 
            });
          }
          
          // Validazione: assicurati che tutti i campi abbiano almeno stringhe vuote
          return {
            [questionKey]: {
              question: message.question || "",
              userAnswer: message.answer || message.userAnswer || "",
              correctAnswer: message.correctAnswer || message.correct_answer || "",
              originalQuestionId: message.id || null,
              questionIndex: questionIndex,
              audioUrl: examAudioUrlMap.get(questionKey) || null,
              lastUpdated: new Date().toISOString(),
              // Per-question evaluation scores (from /get-feedback-with-assistant)
              ...(message.score !== undefined && { score: message.score }),
              ...(message.aiFeedback && { aiFeedback: message.aiFeedback }),
            }
          };
        });
      }
      
      // 🚨 FIX: Se esiste già una sessione, fai merge con i dati esistenti
      if (existingData && existingData.chatMessages) {
        const existingMessages = Array.isArray(existingData.chatMessages) ? existingData.chatMessages : [];
        
        // Converti messaggi esistenti in mappa per merge facile
        const existingMessagesMap = {};
        existingMessages.forEach(msgObj => {
          Object.assign(existingMessagesMap, msgObj);
        });
        
        // Merge con nuovi messaggi (sovrascrive le stesse chiavi, aggiunge le nuove)
        chatMessages.forEach(msgObj => {
          Object.assign(existingMessagesMap, msgObj);
        });
        
        // Converti di nuovo in array di oggetti
        chatMessages = Object.keys(existingMessagesMap).map(key => ({
          [key]: existingMessagesMap[key]
        }));
        
        console.log(`[saveQAExam] Merge completato - Totale messaggi: ${chatMessages.length}`);
      }
    } else {
      // Formato standard per esami in review mode
      try {
        chatMessages = messages.reduce((acc, message) => {
          const [key, qaPairs] = message;
          
          // 🚨 FIX: Gestione robusta per modalità review
          let question = "", userAnswer = "", feedbackText = "", correctAnswer = "", score = 0;
          
          if (Array.isArray(qaPairs) && qaPairs.length >= 2) {
            // Formato standard con array [question, userAnswer, feedback]
            const [questionObj, userAnswerObj, feedbackObj] = qaPairs;
            question = questionObj?.text ?? "";
            userAnswer = userAnswerObj?.text ?? "";
            feedbackText = feedbackObj?.text ?? "";
            correctAnswer = feedbackObj?.correctAnswer ?? "";
            
            // 🚨 FIX: Cerca anche il feedback nell'array
            let feedbackMsg = null;
            for (const msg of qaPairs) {
              if (msg && msg.isFeedback === true) {
                feedbackMsg = msg;
                break;
              }
            }
            
            if (feedbackMsg) {
              // Se il feedback è un oggetto in text, estrai solo il campo feedback
              if (feedbackMsg.text && typeof feedbackMsg.text === 'object') {
                feedbackText = feedbackMsg.text.feedback ?? "";
                if (process.env.NODE_ENV === 'development') {
                  console.log(`[saveQAExam] 📝 Feedback estratto dall'oggetto:`, feedbackText);
                }
              } else {
                feedbackText = feedbackMsg.text ?? "";
              }
              correctAnswer = feedbackMsg.correctAnswer ?? "";
            }
            
            // 🚨 FIX CRITICO: Cerca il punteggio in tutti i messaggi dell'array
            // Il punteggio può essere in qualsiasi messaggio con isFeedback: true
            score = 0;
            for (const msg of qaPairs) {
              if (msg && (msg.isFeedback === true || msg.score !== undefined)) {
                score = msg.score ?? 0;
                if (process.env.NODE_ENV === 'development') {
                  console.log(`[saveQAExam] 🎯 Score trovato: ${score} nel messaggio:`, msg);
                }
                break;
              }
            }
          } else if (qaPairs && typeof qaPairs === 'object') {
            // 🚨 FIX: Formato alternativo dove qaPairs è già un oggetto
            question = qaPairs.question ?? "";
            userAnswer = qaPairs.answer ?? qaPairs.userAnswer ?? "";
            feedbackText = qaPairs.feedback ?? "";
            correctAnswer = qaPairs.correctAnswer ?? "";
            // 🚨 FIX CRITICO: Estrai il punteggio dall'oggetto qaPairs
            score = qaPairs.score ?? 0;
          }
          
          // Log debug per verificare correlazione corretta
          if (process.env.NODE_ENV === 'development') {
            // 🚨 FIX: Validazione del tipo prima di usare substring
            const questionPreview = (typeof question === 'string' && question) ? question.substring(0, 50) : 'N/A';
            const answerPreview = (typeof userAnswer === 'string' && userAnswer) ? userAnswer.substring(0, 50) : (userAnswer ? String(userAnswer).substring(0, 50) : 'N/A');
              console.log(`[saveQAExam] Review mode - Salvando messageId=${key}, questionText="${questionPreview}...", userAnswer="${answerPreview}..."`);
            console.log(`[saveQAExam] Debug data:`, { question: questionPreview, userAnswer: answerPreview, feedback: feedbackText, score: score });
          }
          
          return [
            ...acc,
            {
              [key]: {
                question: question,
                answer: userAnswer,
                feedback: feedbackText,
                correctAnswer: correctAnswer,
                // 🚨 FIX CRITICO: Salva il punteggio nella struttura dati per mantenere il valore
                score: score,
                audioUrl: examAudioUrlMap.get(key) || null,
                lastUpdated: new Date().toISOString()
              }
            }
          ];
        }, []);
      } catch (reduceError) {
        console.error(`[saveQAExam] Errore durante il reduce:`, reduceError);
        console.log(`[saveQAExam] Messages data:`, messages);
        throw reduceError;
      }
    }

    // 🚨 DEBUG: Log dei dati finali prima del salvataggio
    if (process.env.NODE_ENV === 'development') {
      console.log(`[saveQAExam] 💾 DATI FINALI DA SALVARE:`, { chatMessages, reviewMode });
      if (Array.isArray(chatMessages)) {
        chatMessages.forEach((msg, index) => {
          console.log(`[saveQAExam] 💾 ChatMessage ${index}:`, msg);
        });
      }
    }

    // 🚨 FIX: Usa merge: true per preservare altri campi e fare update incrementale
    // `userID` (STU-480): le regole riconoscono il proprietario della sessione
    // dall'esame, ma scriverlo qui la rende leggibile senza un get in piu'.
    const sessionOwner = getAuth().currentUser?.uid;
    await setDoc(examSessionRef, {
      chatMessages,
      reviewMode,
      lastUpdated: new Date().toISOString(),
      ...(sessionOwner ? { userID: sessionOwner } : {})
    }, { merge: true });

    // Pulisci le URL audio già persistite dalla mappa
    if (examAudioUrlMap.size > 0 && Array.isArray(chatMessages)) {
      chatMessages.forEach(msgObj => {
        const key = Object.keys(msgObj)[0];
        if (key) examAudioUrlMap.delete(key);
      });
    }

    try {
      const entriesLen = Array.isArray(chatMessages) ? chatMessages.length : 0;
      console.log(`[saveQAExam] ✅ Sessione esame ${existingData ? 'aggiornata' : 'creata'} ${examID} | voci=${entriesLen}`);
      
      // 🚨 DEBUG: Verifica cosa è stato effettivamente salvato
      if (process.env.NODE_ENV === 'development') {
        const verifyDoc = await getDoc(examSessionRef);
        if (verifyDoc.exists()) {
          console.log(`[saveQAExam] 🔍 VERIFICA SALVATAGGIO:`, verifyDoc.data());
        }
      }
    } catch (_) {}
  } catch (error) {
    console.error("Errore nel salvataggio dell'esame:", error);
    throw error;
  }
};

// Negative cache per l'entitlement: evita di ri-interrogare la subcollection
// subscriptions ad OGNI navigazione per gli utenti FREE. I hook ad alto
// traffico (useUserSync, useGetDocuments, useGetQA) chiamano `clearUserCache`
// PRIMA di getUser, vanificando la cache utente da 5 min → senza questo, ogni
// free user farebbe una query Stripe per ogni cambio pagina. La chiave
// `no_live_sub_${uid}` NON è toccata da clearUserCache (che rimuove solo
// user_data_cache_/user_profile_cache_), quindi sopravvive a quei clear.
// SICUREZZA: si scrive SOLO quando la query ha confermato "nessuna sub live".
// Un utente con sub live non finisce MAI in negative cache → l'entitlement di
// un trial/active reale non può mai essere ritardato o negato da questa cache.
// TTL corto (3 min) → un trial appena avviato viene ri-controllato entro pochi
// minuti anche se l'utente non ricarica.
const NO_LIVE_SUB_TTL_MS = 3 * 60 * 1000;
const noLiveSubKey = userId => `no_live_sub_${userId}`;

const isNoLiveSubCached = userId => {
  try {
    const raw = localStorage.getItem(noLiveSubKey(userId));
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    if (Date.now() - ts < NO_LIVE_SUB_TTL_MS) return true;
    // Scaduta: pulizia opportunistica.
    localStorage.removeItem(noLiveSubKey(userId));
    return false;
  } catch (_) {
    return false;
  }
};

const setNoLiveSubCache = userId => {
  try {
    localStorage.setItem(noLiveSubKey(userId), String(Date.now()));
  } catch (_) { /* no-op */ }
};

export const clearNoLiveSubCache = userId => {
  try {
    localStorage.removeItem(noLiveSubKey(userId));
  } catch (_) { /* no-op */ }
};

/**
 * Pausa attiva sul doc utente. Replica ESATTA della logica di
 * `redux/reducers/authReducer.js` (~righe 32-47): `paused === true` E
 * `pause_until` ancora nel futuro. Stesso parsing eterogeneo di `pause_until`
 * (Firestore Timestamp client/admin, ISO string, millis number).
 * Default difensivo: se `pause_until` è mancante/non interpretabile → NON in
 * pausa (si preferisce dare l'accesso al cliente pagante, come il reducer).
 *
 * @param {Object|null} userData
 * @returns {boolean}
 */
const isUserPaused = userData => {
  if (!userData || typeof userData !== 'object') return false;
  const pauseUntilMs = (() => {
    const pu = userData.pause_until;
    if (pu == null) return null;
    if (typeof pu === 'number') return pu;
    if (typeof pu === 'string') {
      const ms = new Date(pu).getTime();
      return Number.isNaN(ms) ? null : ms;
    }
    if (typeof pu.toMillis === 'function') return pu.toMillis();
    if (typeof pu.seconds === 'number') return pu.seconds * 1000;
    if (typeof pu._seconds === 'number') return pu._seconds * 1000;
    return null;
  })();
  return userData.paused === true && pauseUntilMs !== null && pauseUntilMs > Date.now();
};

/**
 * Entitlement source-of-truth (webhook-independent).
 *
 * Il campo `students/{uid}.plan` NON viene messo a 'Premium' durante un trial
 * card-required (gap di un webhook server-side): un utente in `trialing` resta
 * 'Base' su Firestore pur essendo a tutti gli effetti Premium. Il segnale
 * affidabile è la subcollection `students/{uid}/subscriptions`, scritta
 * dall'estensione firestore-stripe con `status: 'trialing' | 'active'`.
 *
 * Lettura ONE-SHOT (getDocs, niente listener che resta appeso) e best-effort:
 * - nessun utente autenticato / uid non combaciante  → false (no query)
 * - qualsiasi errore (rete, permessi, SSR)            → false
 * Non lancia mai: l'entitlement è additivo, in dubbio non si fa upgrade.
 *
 * Negative cache (TTL 3 min): se una query precedente ha confermato "nessuna
 * sub live", si ritorna false SENZA ri-interrogare. Mai cachato il caso
 * positivo → un utente con sub live non viene mai short-circuitato.
 *
 * @param {string} userId
 * @returns {Promise<boolean>} true se esiste una subscription trialing/active
 */
export const hasLiveTrialingOrActiveSubscription = async userId => {
  try {
    if (!userId) return false;
    // Non interrogare se l'auth non è pronta o l'uid non combacia (evita query
    // con utente sbagliato in stati transitori di login/logout).
    const currentUid = getAuth(app).currentUser?.uid;
    if (!currentUid || currentUid !== userId) return false;

    // Negative cache: free user senza sub → non ri-interrogare ad ogni nav.
    if (isNoLiveSubCached(userId)) return false;

    const subscriptionsRef = collection(firestore, "students", userId, "subscriptions");
    const q = query(subscriptionsRef, where("status", "in", ["trialing", "active"]));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Solo il caso negativo viene cachato (vedi nota sul negative cache).
      setNoLiveSubCache(userId);
      return false;
    }
    // Sub live trovata: rimuovi eventuale negative cache residua così non la
    // nasconde su letture successive entro il TTL.
    clearNoLiveSubCache(userId);
    return true;
  } catch (error) {
    // Best-effort: una lettura entitlement fallita non deve mai degradare un
    // utente né rompere il login. In dubbio: nessun upgrade. NON si scrive
    // negative cache su errore (un errore transitorio non è una conferma di
    // assenza sub).
    if (process.env.NODE_ENV === 'development') {
      console.warn("hasLiveTrialingOrActiveSubscription fallita (best-effort):", error?.message || error);
    }
    return false;
  }
};

/**
 * Applica l'entitlement EFFETTIVO al doc utente: se c'è una subscription live
 * (trialing/active) il piano risultante è 'Premium', anche quando Firestore
 * riporta ancora 'Base' (gap webhook). REGOLA ADDITIVA: non declassa mai
 * nessuno — chi è già 'Premium' resta tale, gli altri vengono promossi SOLO se
 * hanno una sub live. Per ogni altro caso il doc è restituito invariato.
 *
 * Guard PAUSA: Stripe `pause_collection` mantiene la subscription `status:
 * 'active'` (la query subcollection matcherebbe), ma un abbonamento in pausa
 * NON deve essere promosso a Premium. Replichiamo qui la stessa rilevazione del
 * reducer (isUserPaused) per non dipendere da quell'accoppiamento. Resta
 * ADDITIVO: un utente in pausa viene lasciato invariato (nessun upgrade), mai
 * declassato.
 *
 * @param {string} userId
 * @param {Object|null} userData - il doc `students/{uid}` grezzo
 * @returns {Promise<Object|null>} userData con `plan` effettivo
 */
const applyEffectivePremium = async (userId, userData) => {
  if (!userData || typeof userData !== 'object') return userData;
  // Già Premium da Firestore: nessuna query, nessuna modifica.
  if (userData.plan === 'Premium') return userData;

  // Pausa attiva: non promuovere (pause_collection lascia status 'active').
  // Lasciato invariato → additivo, mai un downgrade.
  if (isUserPaused(userData)) return userData;

  const hasLiveSub = await hasLiveTrialingOrActiveSubscription(userId);
  if (!hasLiveSub) return userData;

  // Promozione effettiva (trial card-required o subscription attiva non ancora
  // riflessa su `plan`). Tutti gli altri campi restano intatti.
  return { ...userData, plan: 'Premium' };
};

/**
 * Attende che l'entitlement Premium sia EFFETTIVO subito dopo il checkout
 * (anti-rimborso P0). Appena tornato da Stripe, il webhook
 * `checkout.session.completed` (che scrive `students/{uid}.plan = 'Premium'`) e
 * la subcollection `subscriptions` scritta dall'estensione sono ASINCRONI: una
 * lettura troppo precoce vede ancora 'Base', cacha quel verdetto (user cache 5
 * min) e avvelena la negative-cache `no_live_sub_` (3 min, sopravvive a
 * clearUserCache) → il pagante non vede Premium → rimborso/chargeback.
 *
 * Questo helper, da usare SOLO sulla schermata di successo:
 *  - pulisce PRIMA la user cache + la negative-cache (lettura fresca);
 *  - apre due listener `onSnapshot` (bounded) e risolve `true` al PRIMO segnale:
 *      Path A: `students/{uid}.plan === 'Premium'` (webhook), oppure
 *      Path B: subscription `trialing|active` (estensione), se non in pausa;
 *  - NON scrive MAI alcuna cache (in particolare mai un verdetto non-Premium):
 *    non può quindi avvelenare nulla su questa superficie;
 *  - al timeout risolve `false` (il chiamante naviga comunque, cache pulite →
 *    /home risolve l'entitlement appena i write atterrano).
 *
 * Additivo e best-effort: non lancia mai, non declassa nessuno. Per gli utenti
 * GIÀ Premium risolve all'istante (primo snapshot del doc).
 *
 * @param {string} userId
 * @param {{ timeoutMs?: number }} [opts]
 * @returns {Promise<boolean>} true se l'entitlement Premium è confermato entro il bound
 */
export const waitForPremiumEntitlement = async (userId, { timeoutMs = 15000 } = {}) => {
  if (!userId) return false;
  // Auth non pronta / uid non combaciante → niente listener (evita query con
  // utente sbagliato in stati transitori). In dubbio: nessuna conferma.
  const currentUid = getAuth(app).currentUser?.uid;
  if (!currentUid || currentUid !== userId) return false;

  // Lettura fresca: rimuovi le stesse chiavi di clearUserCache + la negative-cache.
  try { localStorage.removeItem(`user_data_cache_${userId}`); } catch (_) { /* no-op */ }
  try { localStorage.removeItem(`user_profile_cache_${userId}`); } catch (_) { /* no-op */ }
  clearNoLiveSubCache(userId);

  return new Promise(resolve => {
    let settled = false;
    let timeoutId = null;
    const unsubs = [];
    // Stato dei due listener. `tryResolve` valuta l'entitlement effettivo a OGNI
    // snapshot → l'esito non dipende dall'ordine di arrivo (doc vs subs).
    let latestUserData = null; // ultimo doc utente visto (per il guard pausa)
    let docSeen = false;       // il doc utente è arrivato almeno una volta
    let hasLiveSub = false;    // esiste una subscription trialing|active
    const finish = val => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      unsubs.forEach(u => { try { u(); } catch (_) { /* no-op */ } });
      resolve(val);
    };
    const tryResolve = () => {
      // Coerente con applyEffectivePremium:
      //  - plan === 'Premium' ⇒ Premium incondizionato (anche se in pausa: la
      //    pausa non declassa chi ha già 'Premium' sul doc);
      //  - altrimenti Premium SOLO se c'è una sub live E il doc è stato visto E
      //    NON è in pausa. Richiedere `docSeen` chiude la race in cui la subs
      //    arriva prima del doc e un utente in PAUSA (pause_collection lascia
      //    status 'active') verrebbe promosso con `latestUserData` ancora null.
      if (latestUserData && latestUserData.plan === 'Premium') { finish(true); return; }
      if (hasLiveSub && docSeen && !isUserPaused(latestUserData)) finish(true);
    };

    // Bound: oltre il quale si naviga comunque (cache lasciate pulite).
    timeoutId = setTimeout(() => finish(false), timeoutMs);

    // Path A — webhook: students/{uid}.plan = 'Premium'. Copre anche il one-time
    // (Maturità), che NON crea alcuna subscription.
    try {
      const userRef = doc(firestore, "students", userId);
      unsubs.push(onSnapshot(
        userRef,
        snap => {
          const data = snap.data();
          latestUserData = data || null;
          docSeen = true;
          tryResolve();
        },
        () => { /* errore listener: best-effort, decide il timeout o il Path B */ },
      ));
    } catch (_) { /* no-op */ }

    // Path B — estensione firestore-stripe: subscription trialing|active.
    try {
      const subsRef = collection(firestore, "students", userId, "subscriptions");
      const subsQuery = query(subsRef, where("status", "in", ["trialing", "active"]));
      unsubs.push(onSnapshot(
        subsQuery,
        snap => {
          hasLiveSub = !snap.empty;
          tryResolve();
        },
        () => { /* best-effort */ },
      ));
    } catch (_) { /* no-op */ }
  });
};

// get user data
export const getUser = async userId => {
  try {
    // Check cache first
    const cachedData = localStorage.getItem(`user_data_cache_${userId}`);
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        
        // Use cache if it's less than 5 minutes old and data is valid
        if (cacheAge < 5 * 60 * 1000 && data && typeof data === 'object' && data.email) {
          console.log("Utilizzando dati utente dalla cache");
          return data;
        } else {
          // Cache scaduta o dati non validi, rimuovi la cache
          localStorage.removeItem(`user_data_cache_${userId}`);
        }
      } catch (cacheError) {
        console.error("Errore nel parsing della cache utente:", cacheError);
        // Rimuovi cache corrotta
        localStorage.removeItem(`user_data_cache_${userId}`);
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Recuperando dati utente da Firestore per UID: ${userId}`);
    }
    const userRef = doc(firestore, "students", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.error(`Documento utente non trovato in Firestore per UID: ${userId}`);
      return null;
    }
    
    const rawUserData = userSnap.data();

    // Valida che i dati dell'utente siano completi
    if (!rawUserData || typeof rawUserData !== 'object' || !rawUserData.email) {
      console.error("Dati utente incompleti o non validi:", rawUserData);
      return null;
    }

    // Entitlement EFFETTIVO (webhook-independent): se esiste una subscription
    // live (trialing/active) il piano diventa 'Premium' anche se Firestore
    // riporta ancora 'Base'. Additivo (mai declassa). Applicato PRIMA della
    // cache così i 72 gate `user.plan === 'Premium'` vedono il valore corretto
    // su ogni `setUser` che passa da getUser, e le cache-hit successive restano
    // Premium senza ulteriori letture Firestore (entro i 5 min di TTL).
    const userData = await applyEffectivePremium(userId, rawUserData);

    // Save to cache with timestamp only if data is valid
    try {
      localStorage.setItem(`user_data_cache_${userId}`, JSON.stringify({
        data: userData,
        timestamp: Date.now()
      }));
      if (process.env.NODE_ENV === 'development') {
        console.log("Dati utente salvati nella cache");
      }
    } catch (storageError) {
      console.error("Errore nel salvataggio della cache utente:", storageError);
    }
    
    return userData;
  } catch (error) {
    console.error("Errore in getUser:", error);
    throw error;
  }
};

// Get document by id
export const getDocumentById = async documentId => {
  try {
    const docRef = doc(firestore, "documents", documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const documentData = docSnap.data();

      // Se il testo è salvato su Storage, recuperalo
      let text = documentData.text;
      if (documentData.isStoredInStorage && documentData.textStorageRef) {
        try {
          console.log(`📥 Recupero testo da Storage: ${documentData.textStorageRef}`);
          const textRef = ref(storage, documentData.textStorageRef);
          const downloadUrl = await getDownloadURL(textRef);
          const response = await fetch(downloadUrl);
          text = await response.text();
          console.log(`✅ Testo recuperato da Storage (${text.length} caratteri)`);
        } catch (storageError) {
          console.error('❌ Errore nel recupero del testo da Storage:', storageError);
          text = '[Errore nel recupero del testo]';
        }
      }

      // Estrai i campi principali
      return {
        id: documentId,
        ...documentData,
        text, // Usa il testo recuperato da Storage se necessario
        uploadDate: documentData.uploadDate?.toDate?.() || new Date(),
        lastModified: documentData.lastModified?.toDate?.() || new Date()
      };
    } else {
      throw new Error('Documento non trovato');
    }
  } catch (error) {
    console.error('Errore nel recupero del documento:', error);
    throw error;
  }
};

/**
 * Get multiple documents by their IDs
 * Useful for loading documents that belong to a folder but haven't been loaded yet
 * @param {string[]} documentIds - Array of document IDs to fetch
 * @returns {Promise<Array>} - Array of document objects
 */
export const getDocumentsByIds = async (documentIds) => {
  try {
    if (!documentIds || documentIds.length === 0) {
      return [];
    }

    const userId = JSON.parse(localStorage.getItem("userId"));
    if (!userId) {
      throw new Error("User ID non trovato");
    }

    // Un get per id (STU-480, vedi leggiDocumentiPerId): una cartella che
    // contiene l'id di un documento cancellato faceva cadere la query `in`
    // intera. Restano solo i documenti propri, come prima col filtro
    // `uploadedBy`.
    const trovati = await leggiDocumentiPerId(documentIds);
    const results = [];
    documentIds.forEach((id) => {
      const docData = trovati[id];
      if (!docData || docData.uploadedBy !== userId) return;
      results.push({
        id,
        ...docData,
        uploadDate: docData.uploadDate?.toDate?.() || new Date(),
        lastModified: docData.lastModified?.toDate?.() || new Date()
      });
    });

    console.log(`📂 getDocumentsByIds: Recuperati ${results.length}/${documentIds.length} documenti`);
    return results;
  } catch (error) {
    console.error("Errore in getDocumentsByIds:", error);
    return [];
  }
};

// ============================================
// MATURITA 2026 - Query per materia
// ============================================

/**
 * Recupera tutti i documenti di un utente filtrati per materia
 * @param {string} userId - ID utente
 * @param {string} materia - ID materia (es. 'matematica')
 * @returns {Promise<Array>} Lista documenti
 */
export const getDocumentsByMateria = async (userId, materia) => {
  try {
    if (!userId || !materia) return [];

    const documentsRef = collection(firestore, 'documents');
    const q = query(
      documentsRef,
      where('uploadedBy', '==', userId),
      where('materia', '==', materia),
      orderBy('uploadDate', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Errore in getDocumentsByMateria:', error);
    return [];
  }
};

/**
 * Recupera tutte le QA aggregate da tutti i documenti di una materia
 * @param {string} userId - ID utente
 * @param {string} materia - ID materia
 * @returns {Promise<Array>} Lista QA pairs
 */
export const getMaturitaQAByMateria = async (userId, materia) => {
  try {
    if (!userId || !materia) return [];

    // Prima ottieni tutti i documenti della materia
    const documents = await getDocumentsByMateria(userId, materia);
    if (documents.length === 0) return [];

    const allQA = [];

    // Per ogni documento, recupera le QA dalla subcollection
    for (const document of documents) {
      try {
        const qaCollectionRef = collection(firestore, 'questions', document.id, 'qa');
        const qaSnapshot = await getDocs(qaCollectionRef);

        qaSnapshot.docs.forEach((qaDoc) => {
          const data = qaDoc.data();
          // Filtra solo domande di tipo exam/interrogation
          if (data.type === 'exam' || !data.type) {
            allQA.push({
              id: qaDoc.id,
              documentId: document.id,
              documentTitle: document.title || document.file_name,
              materia,
              question: data.question,
              answer: data.answer || data.correct_answer,
              type: data.type || 'exam',
              ...data,
            });
          }
        });
      } catch (qaError) {
        console.error(`Errore QA per documento ${document.id}:`, qaError);
      }
    }

    return allQA;
  } catch (error) {
    console.error('Errore in getMaturitaQAByMateria:', error);
    return [];
  }
};

// get user's exams in progress based on user ID and document ID - OPTIMIZED
export const getExamsInProgress = async ({ limitCount = 5, startAfterDoc = null } = {}) => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    if (!userId) {
      throw new Error("User ID non trovato");
    }

    const constraints = [
      where("userID", "==", userId),
      where("status", "==", "in-progress"),
      orderBy("examEnd", "desc"),
      orderBy(documentId()),
      limit(limitCount)
    ];

    if (startAfterDoc) {
      constraints.push(startAfter(startAfterDoc));
    }

    const examsQuery = query(
      collection(firestore, "exam"),
      ...constraints
    );

    const examsSnapshot = await getDocs(examsQuery);
    
    // Se non ci sono esami, ritorna subito
    if (examsSnapshot.empty) {
      return {
        exams: [],
        lastDoc: null,
        hasMore: false
      };
    }

    // STEP 2: Estrai tutti gli esami
    const examDocs = examsSnapshot.docs;
    const allExams = examDocs.map(docSnap => {
      const examData = docSnap.data();
      console.log(`📊 Esame ${docSnap.id} dati completi:`, examData);
      return {
        examID: docSnap.id,
        ...examData,
      };
    })
    // Ordina gli esami per examEnd (data più recente prima) lato client
    .sort((a, b) => {
      const dateA = a.examEnd?.toDate ? a.examEnd.toDate() : (a.examEnd?.seconds ? new Date(a.examEnd.seconds * 1000) : new Date(0));
      const dateB = b.examEnd?.toDate ? b.examEnd.toDate() : (b.examEnd?.seconds ? new Date(b.examEnd.seconds * 1000) : new Date(0));
      return dateB - dateA; // Ordine decrescente (più recente prima)
    });

    // STEP 3: Estrai gli ID dei documenti necessari per tutti gli esami
    const documentIds = new Set();
    allExams.forEach(exam => {
      if (exam.documentID) {
        documentIds.add(exam.documentID);
      }
    });

    // STEP 4: Carica SOLO i documenti correlati agli esami
    const documentsData = {};
    
    if (documentIds.size > 0) {
      // Un get per id, non `in` a lotti (STU-480): vedi leggiDocumentiPerId.
      const trovati = await leggiDocumentiPerId(Array.from(documentIds));
      Object.entries(trovati).forEach(([id, data]) => {
        documentsData[id] = data;

        // Salva anche la mappatura contentUUID -> documentID
        if (data.contentUUID) {
          documentsData[data.contentUUID] = { ...data, originalDocId: id };
        }
      });
    }

    // STEP 5: Unisci i dati degli esami con i dati dei documenti
    const examsWithDocuments = allExams.map(exam => {
      // Cerca prima per documentID
      let documentData = documentsData[exam.documentID];
      
      // Se non trova per documentID, prova con contentUUID
      if (!documentData && exam.contentUUID) {
        documentData = documentsData[exam.contentUUID];
        
        if (documentData) {
          // Aggiorna il documentID dell'esame con l'ID originale
          if (documentData.originalDocId) {
            exam.linkedDocumentID = exam.documentID; // Salva l'ID originale
            exam.documentID = documentData.originalDocId; // Aggiorna con l'ID corretto
          }
        }
      }
      
      if (documentData) {
        return {
          ...exam,
          ...documentData,
          savedAtQuestionNumber: exam.savedAtQuestionNumber,
          timeRemaining: exam.timeRemaining,
        };
      }
      return null;
    }).filter(exam => exam !== null); // Filtra gli esami nulli

    return {
      exams: examsWithDocuments,
      lastDoc: examDocs[examDocs.length - 1],
      hasMore: examDocs.length === limitCount
    };

  } catch (error) {
    console.error("Errore nel recupero degli esami:", error);
    throw error;
  }
};

// get user's completed exams
export const getCompletedExams = async ({ limitCount = 5, startAfterDoc = null } = {}) => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    if (!userId) {
      throw new Error("User ID non trovato");
    }

    const constraints = [
      where("userID", "==", userId),
      where("status", "==", "completed"),
      orderBy("examEnd", "desc"),
      orderBy(documentId()),
      limit(limitCount)
    ];

    if (startAfterDoc) {
      constraints.push(startAfter(startAfterDoc));
    }

    const examsQueryRef = query(
      collection(firestore, "exam"),
      ...constraints
    );

    const examsSnapshot = await getDocs(examsQueryRef);
    if (examsSnapshot.empty) {
      return {
        exams: [],
        lastDoc: null,
        hasMore: false
      };
    }

    const examDocs = examsSnapshot.docs;
    const exams = examDocs.map(docSnap => ({ examID: docSnap.id, ...docSnap.data() }));

    const documentIds = new Set();
    exams.forEach(exam => {
      if (exam.documentID) documentIds.add(exam.documentID);
      if (exam.contentUUID) documentIds.add(exam.contentUUID);
    });

    const documentsData = {};
    if (documentIds.size > 0) {
      // Un get per id, non `in` a lotti (STU-480): vedi leggiDocumentiPerId.
      const trovati = await leggiDocumentiPerId(Array.from(documentIds));
      Object.entries(trovati).forEach(([id, data]) => {
        documentsData[id] = data;
        if (data.contentUUID) {
          documentsData[data.contentUUID] = { ...data, originalDocId: id };
        }
      });
    }

    const enriched = exams.map(exam => {
      let documentData = documentsData[exam.documentID];
      if (!documentData && exam.contentUUID) {
        documentData = documentsData[exam.contentUUID];
        if (documentData?.originalDocId) {
          exam.linkedDocumentID = exam.documentID;
          exam.documentID = documentData.originalDocId;
        }
      }
      if (documentData) {
        return {
          ...exam,
          ...documentData,
        };
      }
      return null;
    }).filter(Boolean);

    enriched.sort((a, b) => {
      const aEnd = a.examEnd?.toMillis?.() || a.examEnd?.toDate?.()?.getTime?.() || a.examEnd || 0;
      const bEnd = b.examEnd?.toMillis?.() || b.examEnd?.toDate?.()?.getTime?.() || b.examEnd || 0;
      return bEnd - aEnd;
    });

    return {
      exams: enriched.filter(e => e.documentID),
      lastDoc: examDocs[examDocs.length - 1],
      hasMore: examDocs.length === limitCount
    };
  } catch (error) {
    console.error("Errore nel recupero degli esami completati:", error);
    throw error;
  }
};

// exam logic
export const getQAExamSessions = async examID => {
  try {
    const examSessionRef = doc(firestore, "examSessions", examID);
    const examSessionSnap = await getDoc(examSessionRef);
    const { chatMessages, reviewMode } = examSessionSnap.data();

    // Normalizza chatMessages: può essere un array (formato nuovo) o un oggetto mappa (formato legacy)
    const entries = Array.isArray(chatMessages)
      ? chatMessages
      : (chatMessages && typeof chatMessages === 'object')
        ? Object.keys(chatMessages).map((k) => ({ [k]: chatMessages[k] }))
        : [];

    // Log recupero sessione d'esame in corso
    try {
      const entriesLen = entries.length;
      let firstKey = null;
      if (entriesLen > 0 && typeof entries[0] === 'object') {
        const keys = Object.keys(entries[0] || {});
        firstKey = keys && keys[0] ? keys[0] : null;
      }
      console.log(`[getQAExamSessions] Caricata sessione esame ${examID} | reviewMode=${reviewMode} | voci=${entriesLen}${firstKey ? ` | firstKey=${firstKey}` : ''}`);
    } catch (_) {}

    console.log(entries);
    // Gestione formato interrogazione
    if (reviewMode === false) {
      return {
        messages: entries.map(message => {
          const key = Object.keys(message)[0];
          const qaData = message[key];
          return {
            id: key, // Manteniamo l'ID univoco generato
            index: qaData.questionIndex || 0, // Usa l'indice salvato, non quello estratto dalla chiave
            question: qaData.question,
            answer: qaData.userAnswer,
            correct_answer: qaData.correctAnswer,
            originalQuestionId: qaData.originalQuestionId // ID originale della domanda per riferimento
          };
        }),
        reviewMode
      };
    }  

    // Formato standard per esami (Ripetizione): normalizza testi per compatibilità UI
    const normalizeText = (val) => {
      if (typeof val === 'string') return val;
      if (val && typeof val === 'object') {
        // Campo text può essere stringa
        if (typeof val.text === 'string') return val.text;
        // Oppure oggetto con sottocampi noti
        if (val.text && typeof val.text === 'object') {
          if (typeof val.text.question === 'string') return val.text.question;
          if (typeof val.text.content === 'string') return val.text.content;
          if (typeof val.text.feedback === 'string') return val.text.feedback;
        }
        // Fallback su altri alias comuni
        if (typeof val.question === 'string') return val.question;
        if (typeof val.content === 'string') return val.content;
        if (typeof val.feedback === 'string') return val.feedback;
      }
      return '';
    };

    const messagesChat = entries.map(message => {
      const key = Object.keys(message)[0];
      const { question, answer, feedback, score } = message[key];

      // 🚨 DEBUG: Log per verificare che lo score venga caricato correttamente
      if (process.env.NODE_ENV === 'development') {
        console.log(`[getQAExamSessions] Caricando messageId=${key}, score=${score}`);
      }

      const q = normalizeText(question);
      const a = normalizeText(answer);
      const f = normalizeText(feedback);

      const messages = [
        q ? { id: key, text: q, sender: 'bot' } : null,
        a ? { id: key, text: a, sender: 'user' } : null,
        f ? { id: key, text: f, sender: 'user', isFeedback: true, score: score || 0 } : null,
      ].filter(Boolean);

      return [key, messages];
    });

    return { messages: messagesChat };
  } catch (error) {
    throw error;
  }
};

// check if exam is in progress
export const isExamInProgress = async (examID, contentUUID = null) => {
  try {
    // Verifica diretta per ID
    const examDocRef = doc(firestore, "exam", examID);
    const examSessionSnap = await getDoc(examDocRef);

    // Se l'esame esiste direttamente, restituisci l'ID
    if (examSessionSnap.exists()) {
      return examSessionSnap.id;
    }
    
    // Se non esiste e abbiamo un contentUUID, cerchiamo esami collegati a quel contentUUID
    // SOLO fra i propri (STU-480): senza `userID` la query poteva agganciare
    // l'esame in corso di un altro studente con lo stesso contentUUID
    // deterministico (`cuuid_*` dei materiali per argomento), e con le regole
    // nuove viene negata.
    const ownerId = getAuth().currentUser?.uid || JSON.parse(localStorage.getItem("userId") || "null");
    if (!examSessionSnap.exists() && contentUUID && ownerId) {
      
      const contentUUIDQuery = query(
        collection(firestore, "exam"),
        where("contentUUID", "==", contentUUID),
        where("status", "==", "in-progress"),
        where("userID", "==", ownerId)
      );
      
      const contentUUIDResults = await getDocs(contentUUIDQuery);
      
      if (!contentUUIDResults.empty) {
        const matchingExam = contentUUIDResults.docs[0];
        return matchingExam.id;
      }
    }

    return null;
  } catch (error) {
    console.log(error.message);
    throw error;
  }
};

// delete exam from Firestore
export const deleteExamFromFirestore = async (examID) => {
  try {
    const examRef = doc(firestore, "exam", examID);
    const examSnap = await getDoc(examRef);
    if (!examSnap.exists()) {
      throw new Error("L'esame non esiste");
    }
    const examData = examSnap.data();

    // Prima la sessione, poi l'esame (STU-480): le regole riconoscono il
    // proprietario di una sessione senza `userID` dall'esame, quindi a esame
    // gia' cancellato la sessione non si poteva piu' attribuire. La sessione
    // puo' anche non esistere: non deve fermare la cancellazione.
    const examSessionRef = doc(firestore, "examSessions", examID);
    try {
      await deleteDoc(examSessionRef);
    } catch (sessionError) {
      console.warn("Sessione d'esame non cancellata:", sessionError?.message);
    }

    // Elimina l'esame
    await deleteDoc(examRef);

    // Aggiorna il contatore degli esami tentati per l'utente
    const userID = JSON.parse(localStorage.getItem("userId"));
    if (userID) {
      const userRef = doc(firestore, "students", userID);
      await updateDoc(userRef, {
        examsAttempted: increment(-1)
      });
    }

    // Se l'esame era associato a un documento, aggiorna il documento.
    // Solo il proprietario scrive sul documento (STU-480): sul demo o su un
    // documento condiviso dal docente il flag non si tocca, e l'esame e'
    // comunque cancellato.
    if (examData.documentID) {
      const documentRef = doc(firestore, "documents", examData.documentID);
      try {
        await updateDoc(documentRef, {
          hasExamInProgress: false
        });
      } catch (flagError) {
        console.warn("hasExamInProgress non aggiornato:", flagError?.message);
      }
    }

    return true;
  } catch (error) {
    console.error("Errore durante l'eliminazione dell'esame:", error);
    throw error;
  }
};

// Funzioni per la gestione delle cartelle

/**
 * Salva una cartella su Firebase
 * @param {Object} folder - Dati della cartella
 * @returns {Object} La cartella salvata con ID aggiornato
 */
export const saveFolder = async (folder) => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    
    // Se è una sottocartella (ha un parentId), aggiungiamola alla cartella genitore invece di creare un nuovo documento
    if (folder.parentId) {
      // Ottieni l'ID effettivo della cartella genitore (rimuovi il prefisso 'firebase_' se presente)
      const parentId = folder.parentId.startsWith('firebase_') 
        ? folder.parentId.replace('firebase_', '') 
        : folder.parentId;
      
      // Elenco di tutti i possibili ID che potrebbero rappresentare la stessa cartella genitore
      const possibleParentIds = [
        parentId,
        `firebase_${parentId}`,
        parentId.replace('subfolder_', '')
      ];
      
      if (parentId.includes('_parent_')) {
        const parts = parentId.split('_parent_');
        possibleParentIds.push(parts[0]); // ID della sottocartella (prima parte)
        if (parts.length > 1) {
          possibleParentIds.push(parts[1]); // ID del parent (seconda parte)
          possibleParentIds.push(`firebase_${parts[1]}`);
        }
      }
      
      // Rimuovi duplicati
      const uniqueParentIds = [...new Set(possibleParentIds)];
      
      // Mantieni comunque un ID principale per la ricerca iniziale
      let cleanParentId = parentId;
      if (parentId.includes('subfolder_') && parentId.includes('_parent_')) {
        const parts = parentId.split('_parent_');
        if (parts.length > 1) {
          cleanParentId = parts[1]; // Prendiamo l'ID del parent
        }
      } else if (parentId.includes('_parent_')) {
        const parts = parentId.split('_parent_');
        if (parts.length > 1) {
          cleanParentId = parts[1]; // Prendiamo l'ID del parent
        }
      }
      
      // Verifica se il genitore è una sottocartella
      const isParentSubfolder = parentId.includes('subfolder_');
      let parentFolderRef;
      let parentFolderSnap;
      
      if (isParentSubfolder) {
        // Se il genitore è una sottocartella, dobbiamo ottenerlo in modo diverso
        // Prova a cercare la cartella genitore usando tutti i possibili ID
        let found = false;
        
        // Prova prima i documenti standalone
        for (const potentialId of uniqueParentIds) {
          if (found) continue;
          
          parentFolderRef = doc(firestore, "folders", potentialId);
          parentFolderSnap = await getDoc(parentFolderRef);
          
          if (parentFolderSnap.exists()) {
            found = true;
            break;
          }
        }
        
        // Se non abbiamo trovato nessuna cartella standalone, cerca nelle sottocartelle
        if (!found) {
          
          // Ottieni tutte le cartelle dell'utente
          const foldersRef = collection(firestore, "folders");
          const q = query(foldersRef, where("userId", "==", userId));
          const snapshot = await getDocs(q);
          
          // Cerca in tutte le cartelle se una di esse contiene la sottocartella che stiamo cercando
          let foundParentFolder = null;
          let parentOfParentId = null;
          
          snapshot.forEach(doc => {
            if (found) return;
            
            const folderData = doc.data();
            
            if (folderData.subfolders && folderData.subfolders.length > 0) {
              // Cerca la sottocartella per ID tra tutti i possibili ID
              for (const potentialId of uniqueParentIds) {
                const subfolder = folderData.subfolders.find(sf => {
                  const matches = (
                    sf.id === potentialId || 
                    (sf.id && sf.id.includes(potentialId))
                  );
                  return matches;
                });
                
                if (subfolder) {
                  foundParentFolder = subfolder;
                  parentOfParentId = doc.id; // ID della cartella che contiene la sottocartella genitore
                  found = true;
                  break;
                }
              }
            }
          });
          
          if (foundParentFolder) {
            // Abbiamo trovato la sottocartella genitore all'interno di un'altra cartella
            // Ora dobbiamo creare una nuova sottocartella all'interno di questa sottocartella
            
            // Genera un ID per la nuova sottocartella
            const subfolderId = `subfolder_${new Date().getTime()}_${Math.random().toString(36).substr(2, 9)}_parent_${cleanParentId}`;
            
            // Crea la sottocartella
            const subfolder = {
              ...folder,
              id: subfolderId,
              userId,
              parentId: cleanParentId,
              createdAt: new Date().toISOString(),
              lastModified: new Date().toISOString(),
              subfolders: folder.subfolders || []
            };
            
            // Aggiorna la sottocartella genitore con la nuova sottocartella
            foundParentFolder.subfolders = foundParentFolder.subfolders || [];
            foundParentFolder.subfolders.push(subfolder);
            
            // Aggiorna la cartella principale che contiene la sottocartella genitore
            parentFolderRef = doc(firestore, "folders", parentOfParentId);
            await updateDoc(parentFolderRef, {
              subfolders: snapshot.docs.find(d => d.id === parentOfParentId).data().subfolders.map(sf => {
                if (sf.id === parentId || (sf.id && sf.id.includes(cleanParentId))) {
                  return foundParentFolder;
                }
                return sf;
              }),
              lastModified: serverTimestamp()
            });
            
            // Restituisci la sottocartella con l'ID aggiornato
            return { ...subfolder, id: subfolderId };
          }
          
          // Se arriviamo qui, non abbiamo trovato il genitore da nessuna parte
          throw new Error("Cartella genitore (sottocartella) non trovata");
        }
      } else {
        // Per cartelle normali, procediamo come prima
        parentFolderRef = doc(firestore, "folders", parentId);
        parentFolderSnap = await getDoc(parentFolderRef);
        
        if (!parentFolderSnap.exists()) {
          throw new Error("Cartella genitore non trovata");
        }
      }
      
      const parentFolder = parentFolderSnap.data();
      
      // Genera un ID per la sottocartella
      const subfolderId = `subfolder_${new Date().getTime()}_${Math.random().toString(36).substr(2, 9)}_parent_${parentId}`;
      
      // Crea la sottocartella con timestamp JavaScript normali (non serverTimestamp)
      const subfolder = {
        ...folder,
        id: subfolderId,
        userId,
        parentId: parentId,
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        subfolders: folder.subfolders || []
      };
      
      // Aggiungi la sottocartella all'array subfolders della cartella genitore
      const subfolders = parentFolder.subfolders || [];
      subfolders.push(subfolder);
      
      // Rimuovi i documenti della sottocartella dalla cartella genitore se presenti
      let parentDocumentIds = parentFolder.documentIds || [];
      if (folder.documentIds && folder.documentIds.length > 0) {
        const docsBeforeFilter = [...parentDocumentIds];
        parentDocumentIds = parentDocumentIds.filter(id => !folder.documentIds.includes(id));
      }
      
      // Aggiorna la cartella genitore con la nuova sottocartella
      await updateDoc(parentFolderRef, {
        subfolders,
        documentIds: parentDocumentIds,
        lastModified: serverTimestamp()
      });
      
      // Restituisci la sottocartella con l'ID aggiornato
      return { ...subfolder, id: subfolderId };
    } 
    // Se è una cartella di primo livello (non ha parentId)
    else {
      // Crea la struttura della cartella da salvare con serverTimestamp
      const folderData = {
        ...folder,
        userId,
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        subfolders: folder.subfolders || [] // Aggiungiamo il campo subfolders se non esiste
      };
      
      // Se è una nuova cartella, crea un nuovo documento
      if (!folder.id.startsWith('firebase_')) {
        const folderRef = collection(firestore, "folders");
        const newFolderDoc = await addDoc(folderRef, folderData);
        return { ...folderData, id: newFolderDoc.id };
      } 
      // Se ha già un ID Firebase, aggiorna il documento esistente
      else {
        const actualId = folder.id.replace('firebase_', '');
        const folderRef = doc(firestore, "folders", actualId);
        await setDoc(folderRef, folderData);
        return { ...folderData, id: actualId };
      }
    }
  } catch (error) {
    console.error("Errore nel salvataggio della cartella:", error);
    throw new Error("Errore nel salvataggio della cartella");
  }
};

/**
 * Aggiorna una cartella esistente su Firebase
 * @param {string} folderId - ID della cartella da aggiornare
 * @param {Object} updates - Campi da aggiornare
 */
export const updateFolder = async (folderId, updates) => {
  try {
    // Se l'ID ha il prefisso 'firebase_', rimuovilo
    const actualId = folderId.startsWith('firebase_') 
      ? folderId.replace('firebase_', '') 
      : folderId;
    
    const folderRef = doc(firestore, "folders", actualId);
    await updateDoc(folderRef, {
      ...updates,
      lastModified: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Errore nell'aggiornamento della cartella:", error);
    throw new Error("Errore nell'aggiornamento della cartella");
  }
};

// Alias per garantire compatibilità con altre parti del codice
export const updateFirestoreFolder = updateFolder;

// Elimina una cartella da Firestore
export const deleteFolder = async (folderId, options = {}) => {
  try {
    // Se l'ID ha il prefisso 'firebase_', rimuovilo
    const actualId = folderId.startsWith('firebase_') 
      ? folderId.replace('firebase_', '') 
      : folderId;
    
    // Verifica se è una sottocartella (controlla se l'ID contiene "subfolder_")
    const isSubfolder = actualId.includes('subfolder_');
    
    if (isSubfolder) {
      // Estrai l'ID della cartella genitore
      const parentFolderId = actualId.split('_parent_')[1];
      
      if (!parentFolderId) {
        throw new Error('ID della cartella genitore non trovato');
      }
      
      // Ottieni la cartella genitore
      const parentFolderRef = doc(firestore, 'folders', parentFolderId);
      const parentFolderSnap = await getDoc(parentFolderRef);
      
      if (!parentFolderSnap.exists()) {
        throw new Error('Cartella genitore non trovata');
      }
      
      const parentFolder = parentFolderSnap.data();
      
      // Trova la sottocartella per ottenere i suoi documentIds
      const subfolderToDelete = (parentFolder.subfolders || []).find(
        subfolder => subfolder.id === actualId
      );
      
      // Ottieni i documenti contenuti nella sottocartella
      const documentsInSubfolder = subfolderToDelete ? (subfolderToDelete.documentIds || []) : [];
      
      // Rimuovi la sottocartella dall'array delle sottocartelle
      const updatedSubfolders = (parentFolder.subfolders || []).filter(
        subfolder => subfolder.id !== actualId
      );
      
      // Aggiorna la cartella genitore, aggiungendo i documenti dalla sottocartella eliminata
      await updateDoc(parentFolderRef, {
        subfolders: updatedSubfolders,
        documentIds: [...(parentFolder.documentIds || []), ...documentsInSubfolder]
      });
      
      return true;
    } else {
      // Elimina la cartella normalmente
      const folderRef = doc(firestore, 'folders', actualId);
      
      // Verifica che la cartella esista prima di eliminarla
      const folderSnap = await getDoc(folderRef);
      if (!folderSnap.exists()) {
        throw new Error(`Cartella non trovata: ${actualId}`);
      }
      
      // Recuperiamo i documentIds della cartella che sta per essere eliminata
      // Non serve fare nulla di specifico con loro, poiché il reducer Redux
      // si occuperà di renderli nuovamente visibili nella vista principale
      
      await deleteDoc(folderRef);
      return true;
    }
  } catch (error) {
    console.error('Errore durante l\'eliminazione della cartella:', error);
    throw error;
  }
};

/**
 * Aggiunge un documento a una cartella su Firebase
 * @param {string} folderId - ID della cartella
 * @param {string} documentId - ID del documento da aggiungere
 */
export const addDocumentToFolder = async (folderId, documentId) => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    
    // Verifica se è una sottocartella (ID inizia con 'subfolder_')
    if (folderId.startsWith('subfolder_')) {
      // Ottieni l'ID della cartella genitore dal folderId
      const parts = folderId.split('_parent_');
      if (parts.length < 2) {
        console.error("Formato ID sottocartella non valido:", folderId);
        throw new Error("ID della cartella genitore non trovato");
      }
      
      // Estrai l'ultima parte dell'ID che contiene il vero genitore
      const parentId = parts[parts.length - 1];
      
      if (!parentId) {
        console.error("ID cartella genitore mancante:", folderId);
        throw new Error("ID della cartella genitore non trovato");
      }
      
      // Crea un elenco di possibili ID per il genitore, simile a saveFolder
      const possibleParentIds = [
        parentId,
        `firebase_${parentId}`,
        parentId.replace('subfolder_', '')
      ];
      
      if (parentId.includes('_parent_')) {
        const subParts = parentId.split('_parent_');
        possibleParentIds.push(subParts[0]);
        if (subParts.length > 1) {
          possibleParentIds.push(subParts[1]);
          possibleParentIds.push(`firebase_${subParts[1]}`);
        }
      }
      
      // Rimuovi duplicati
      const uniqueParentIds = [...new Set(possibleParentIds)];
      
      // Flag per tenere traccia se abbiamo trovato la cartella
      let found = false;
      let parentFolderRef;
      let parentFolderSnap;
      let actualParentId;
      
      // Prova a cercare la cartella genitore direttamente usando tutti i possibili ID
      for (const potentialId of uniqueParentIds) {
        if (found) continue;
        
        actualParentId = potentialId;
        parentFolderRef = doc(firestore, "folders", actualParentId);
        parentFolderSnap = await getDoc(parentFolderRef);
        
        if (parentFolderSnap.exists()) {
          found = true;
          break;
        }
      }
      
      // Se non abbiamo trovato una cartella direttamente, cerchiamo nelle sottocartelle
      if (!found) {
        
        // Ottieni tutte le cartelle dell'utente
        const foldersRef = collection(firestore, "folders");
        const q = query(foldersRef, where("userId", "==", userId));
        const snapshot = await getDocs(q);
        
        // Funzione ricorsiva per cercare una sottocartella all'interno delle cartelle
        const findSubfolderRecursively = (parentFolderData, parentFolderId) => {
          if (!parentFolderData.subfolders || parentFolderData.subfolders.length === 0) {
            return null;
          }
          
          // Cerca la sottocartella direttamente nelle sottocartelle di questa cartella
          for (const subfolder of parentFolderData.subfolders) {
            // Cerca corrispondenze con tutti i possibili ID
            for (const potentialId of uniqueParentIds) {
              if (subfolder.id === potentialId || 
                  (subfolder.id && subfolder.id.includes(potentialId))) {
                return {
                  parentDocId: parentFolderId, 
                  parentData: parentFolderData,
                  subfolder: subfolder,
                  subfolderIndex: parentFolderData.subfolders.indexOf(subfolder)
                };
              }
            }
            
            // Cerca in modo ricorsivo nelle sottocartelle
            if (subfolder.subfolders && subfolder.subfolders.length > 0) {
              const nestedResult = findSubfolderRecursively(subfolder, parentFolderId);
              if (nestedResult) return nestedResult;
            }
          }
          
          return null;
        };
        
        // Cerca in tutte le cartelle
        let foundSubfolderInfo = null;
        
        for (const doc of snapshot.docs) {
          const folderData = doc.data();
          
          const result = findSubfolderRecursively(folderData, doc.id);
          if (result) {
            foundSubfolderInfo = result;
            found = true;
            break;
          }
        }
        
        if (foundSubfolderInfo) {
          // Aggiungi il documento alla sottocartella trovata
          const { parentDocId, parentData, subfolder, subfolderIndex } = foundSubfolderInfo;
          
          // Aggiorna i documentIds della sottocartella
          const documentIds = subfolder.documentIds || [];
          if (!documentIds.includes(documentId)) {
            documentIds.push(documentId);
            subfolder.documentIds = documentIds;
            subfolder.lastModified = new Date().toISOString();
            
            // Aggiorna la cartella principale che contiene la sottocartella
            const mainFolderRef = doc(firestore, "folders", parentDocId);
            await updateDoc(mainFolderRef, {
              subfolders: parentData.subfolders,
              lastModified: serverTimestamp()
            });
            
            return true;
          }
        }
        
        if (!found) {
          console.error("Sottocartella genitore non trovata in nessuna cartella:", parentId);
          throw new Error("Cartella genitore non trovata");
        }
      } else {
        // Abbiamo trovato la cartella genitore direttamente
        const parentFolder = parentFolderSnap.data();
        
        // Trova la sottocartella nell'array subfolders
        const subfolders = parentFolder.subfolders || [];
        
        const subfolderIndex = subfolders.findIndex(sf => sf.id === folderId);
        
        if (subfolderIndex === -1) {
          console.error("Sottocartella non trovata nell'array:", folderId);
          throw new Error("Sottocartella non trovata");
        }
        
        // Aggiungi il documento alla sottocartella
        const documentIds = subfolders[subfolderIndex].documentIds || [];
        if (!documentIds.includes(documentId)) {
          documentIds.push(documentId);
          subfolders[subfolderIndex].documentIds = documentIds;
          subfolders[subfolderIndex].lastModified = new Date().toISOString(); // Aggiorna il timestamp della sottocartella
          
          // Rimuovi il documento dalla cartella padre se è presente
          const parentDocumentIds = parentFolder.documentIds || [];
          if (parentDocumentIds.includes(documentId)) {
            const updatedParentDocumentIds = parentDocumentIds.filter(id => id !== documentId);
            
            // Aggiorna la cartella genitore con la sottocartella aggiornata e rimuovi il documento dai documentIds del genitore
            await updateDoc(parentFolderRef, {
              documentIds: updatedParentDocumentIds,
              subfolders,
              lastModified: serverTimestamp()
            });
          } else {
            // Aggiorna solo la sottocartella senza modificare i documentIds del genitore
            await updateDoc(parentFolderRef, {
              subfolders,
              lastModified: serverTimestamp()
            });
          }
          
        }
      }
    } 
    // Se è una cartella di primo livello
    else {
      // Se l'ID ha il prefisso 'firebase_', rimuovilo
      const actualId = folderId.startsWith('firebase_') 
        ? folderId.replace('firebase_', '') 
        : folderId;
      
      const folderRef = doc(firestore, "folders", actualId);
      
      // Ottieni il documento più recente da Firestore
      const folderDoc = await getDoc(folderRef);
      
      if (!folderDoc.exists()) {
        console.error("Cartella non trovata:", actualId);
        throw new Error("Cartella non trovata");
      }
      
      const folderData = folderDoc.data();
      const documentIds = folderData.documentIds || [];
      
      // Aggiungi il documento solo se non è già presente
      if (!documentIds.includes(documentId)) {
        const updatedDocumentIds = [...documentIds, documentId];
        
        // Utilizza una transazione per garantire l'integrità dell'aggiornamento
        try {
          await updateDoc(folderRef, {
            documentIds: updatedDocumentIds,
            lastModified: serverTimestamp()
          });
          
        } catch (updateError) {
          console.error("Errore durante l'aggiornamento della cartella:", updateError);
          throw updateError;
        }
      } else {
      }
    }
    
    return true;
  } catch (error) {
    console.error("Errore nell'aggiunta del documento alla cartella:", error);
    throw new Error("Errore nell'aggiunta del documento alla cartella");
  }
};

/**
 * Aggiunge N documenti a una cartella di PRIMO LIVELLO in una sola scrittura
 * ATOMICA (arrayUnion): niente read-modify-write, niente race tra chiamate
 * concorrenti (audit 14/08 — N thunk paralleli si sovrascrivevano l'array).
 * arrayUnion deduplica da sé e tocca SOLO documentIds + lastModified: gli
 * altri campi della cartella (es. documentContext scritto dall'app iOS)
 * restano intatti.
 * @param {string} folderId - ID cartella root (con o senza prefisso firebase_)
 * @param {string[]} documentIds - ID dei documenti da agganciare
 */
export const addDocumentsToFolderAtomic = async (folderId, documentIds) => {
  const ids = (documentIds || []).filter(Boolean);
  if (!ids.length) return true;
  const actualId = folderId.startsWith('firebase_')
    ? folderId.replace('firebase_', '')
    : folderId;
  const folderRef = doc(firestore, "folders", actualId);
  await updateDoc(folderRef, {
    documentIds: arrayUnion(...ids),
    lastModified: serverTimestamp()
  });
  return true;
};

/**
 * Rimuove un documento da una cartella su Firebase
 * @param {string} folderId - ID della cartella
 * @param {string} documentId - ID del documento da rimuovere
 */
export const removeDocumentFromFolder = async (folderId, documentId) => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    
    // Verifica se è una sottocartella (ID inizia con 'subfolder_')
    if (folderId.startsWith('subfolder_')) {
      // Ottieni l'ID della cartella genitore dal folderId
      const parts = folderId.split('_parent_');
      if (parts.length < 2) {
        console.error("Formato ID sottocartella non valido:", folderId);
        throw new Error("ID della cartella genitore non trovato");
      }
      
      // Estrai l'ultima parte dell'ID che contiene il vero genitore
      const parentId = parts[parts.length - 1];
      
      if (!parentId) {
        console.error("ID cartella genitore mancante:", folderId);
        throw new Error("ID della cartella genitore non trovato");
      }
      
      // Crea un elenco di possibili ID per il genitore
      const possibleParentIds = [
        parentId,
        `firebase_${parentId}`,
        parentId.replace('subfolder_', '')
      ];
      
      if (parentId.includes('_parent_')) {
        const subParts = parentId.split('_parent_');
        possibleParentIds.push(subParts[0]);
        if (subParts.length > 1) {
          possibleParentIds.push(subParts[1]);
          possibleParentIds.push(`firebase_${subParts[1]}`);
        }
      }
      
      // Rimuovi duplicati
      const uniqueParentIds = [...new Set(possibleParentIds)];
      
      // Flag per tenere traccia se abbiamo trovato la cartella
      let found = false;
      let parentFolderRef;
      let parentFolderSnap;
      let actualParentId;
      
      // Prova a cercare la cartella genitore direttamente usando tutti i possibili ID
      for (const potentialId of uniqueParentIds) {
        if (found) continue;
        
        actualParentId = potentialId;
        parentFolderRef = doc(firestore, "folders", actualParentId);
        parentFolderSnap = await getDoc(parentFolderRef);
        
        if (parentFolderSnap.exists()) {
          found = true;
          break;
        }
      }
      
      // Se non abbiamo trovato una cartella direttamente, cerchiamo nelle sottocartelle
      if (!found) {
        
        // Ottieni tutte le cartelle dell'utente
        const foldersRef = collection(firestore, "folders");
        const q = query(foldersRef, where("userId", "==", userId));
        const snapshot = await getDocs(q);
        
        // Funzione ricorsiva per cercare una sottocartella all'interno delle cartelle
        const findSubfolderRecursively = (parentFolderData, parentFolderId) => {
          if (!parentFolderData.subfolders || parentFolderData.subfolders.length === 0) {
            return null;
          }
          
          // Cerca la sottocartella direttamente nelle sottocartelle di questa cartella
          for (const subfolder of parentFolderData.subfolders) {
            // Cerca corrispondenze con tutti i possibili ID
            for (const potentialId of uniqueParentIds) {
              if (subfolder.id === potentialId || 
                  (subfolder.id && subfolder.id.includes(potentialId))) {
                return {
                  parentDocId: parentFolderId, 
                  parentData: parentFolderData,
                  subfolder: subfolder,
                  subfolderIndex: parentFolderData.subfolders.indexOf(subfolder)
                };
              }
            }
            
            // Cerca in modo ricorsivo nelle sottocartelle
            if (subfolder.subfolders && subfolder.subfolders.length > 0) {
              const nestedResult = findSubfolderRecursively(subfolder, parentFolderId);
              if (nestedResult) return nestedResult;
            }
          }
          
          return null;
        };
        
        // Cerca in tutte le cartelle
        let foundSubfolderInfo = null;
        
        for (const doc of snapshot.docs) {
          const folderData = doc.data();
          
          const result = findSubfolderRecursively(folderData, doc.id);
          if (result) {
            foundSubfolderInfo = result;
            found = true;
            break;
          }
        }
        
        if (foundSubfolderInfo) {
          // Rimuovi il documento dalla sottocartella trovata
          const { parentDocId, parentData, subfolder, subfolderIndex } = foundSubfolderInfo;
          
          // Aggiorna il proprio subfolders array per trovare la sottocartella esatta
          const targetSubfolder = foundSubfolderInfo.parentData.subfolders.find(sf => sf.id === folderId);
          
          if (targetSubfolder) {
            
            // Rimuovi il documento dalla sottocartella
            const documentIds = targetSubfolder.documentIds || [];
            targetSubfolder.documentIds = documentIds.filter(id => id !== documentId);
            targetSubfolder.lastModified = new Date().toISOString();
            
            // Aggiorna la cartella principale che contiene la sottocartella
            const mainFolderRef = doc(firestore, "folders", parentDocId);
            await updateDoc(mainFolderRef, {
              subfolders: parentData.subfolders,
              lastModified: serverTimestamp()
            });
            
            return true;
          } else {
            console.error("Sottocartella specifica non trovata nell'array:", folderId);
            throw new Error("Sottocartella specifica non trovata");
          }
        }
        
        if (!found) {
          console.error("Sottocartella genitore non trovata in nessuna cartella:", parentId);
          throw new Error("Cartella genitore non trovata");
        }
      } else {
        // Abbiamo trovato la cartella genitore direttamente
        const parentFolder = parentFolderSnap.data();
        
        // Trova la sottocartella nell'array subfolders
        const subfolders = parentFolder.subfolders || [];
        
        const subfolderIndex = subfolders.findIndex(sf => sf.id === folderId);
        
        if (subfolderIndex === -1) {
          console.error("Sottocartella non trovata nell'array per rimozione:", folderId);
          throw new Error("Sottocartella non trovata");
        }
        
        // Rimuovi il documento dalla sottocartella
        const documentIds = subfolders[subfolderIndex].documentIds || [];
        subfolders[subfolderIndex].documentIds = documentIds.filter(id => id !== documentId);
        subfolders[subfolderIndex].lastModified = new Date().toISOString(); // Aggiorna il timestamp della sottocartella
        
        // Aggiorna la cartella genitore
        await updateDoc(parentFolderRef, {
          subfolders,
          lastModified: serverTimestamp()
        });
        
      }
    } 
    // Se è una cartella di primo livello
    else {
      // Se l'ID ha il prefisso 'firebase_', rimuovilo
      const actualId = folderId.startsWith('firebase_') 
        ? folderId.replace('firebase_', '') 
        : folderId;
      
      const folderRef = doc(firestore, "folders", actualId);
      const folderDoc = await getDoc(folderRef);
      
      if (!folderDoc.exists()) {
        throw new Error("Cartella non trovata");
      }
      
      const folderData = folderDoc.data();
      const documentIds = folderData.documentIds || [];
      
      // Rimuovi il documento
      await updateDoc(folderRef, {
        documentIds: documentIds.filter(id => id !== documentId),
        lastModified: serverTimestamp()
      });
      
    }
    
    return true;
  } catch (error) {
    console.error("Errore nella rimozione del documento dalla cartella:", error);
    throw new Error("Errore nella rimozione del documento dalla cartella");
  }
};

/**
 * Recupera tutte le cartelle dell'utente da Firebase
 * @returns {Array} Array di cartelle
 */
export const getUserFolders = async () => {
  try {
    const userId = JSON.parse(localStorage.getItem("userId"));
    const foldersQuery = query(
      collection(firestore, "folders"),
      where("userId", "==", userId)
    );

    const querySnapshot = await getDocs(foldersQuery);
    
    // Array per le cartelle di primo livello
    const rootFolders = [];
    
    // Mappa i documenti in oggetti cartella
    querySnapshot.docs.forEach(doc => {
      const folderData = doc.data();
      
      // Aggiungi la cartella di primo livello con il prefisso 'firebase_'
      rootFolders.push({
        ...folderData,
        id: `firebase_${doc.id}` // Aggiungi un prefisso per identificare le cartelle Firebase
      });
      
      // Estrai le sottocartelle e aggiungile all'array delle cartelle
      if (folderData.subfolders && folderData.subfolders.length > 0) {
        folderData.subfolders.forEach(subfolder => {
          // Aggiungi la sottocartella con il suo ID originale
          rootFolders.push({
            ...subfolder,
            parentId: `firebase_${doc.id}` // Aggiorna il parentId con il prefisso
          });
        });
      }
    });

    return rootFolders;
  } catch (error) {
    console.error("Errore nel recupero delle cartelle:", error);
    throw new Error("Errore nel recupero delle cartelle dell'utente");
  }
};

// Alias per compatibilità con codice legacy
export const getFolders = getUserFolders;

// Ottieni una cartella specifica per ID
export const getFolderById = async (folderId) => {
  try {
    if (!folderId) {
      console.error('ID cartella non valido');
      return null;
    }
    
    const userId = JSON.parse(localStorage.getItem("userId"));
    if (!userId) {
      console.error('Utente non autenticato');
      return null;
    }
    
    // Normalizza l'ID per gestire sia gli ID con prefisso che senza
    const normalizedId = folderId.startsWith('firebase_') ? 
      folderId.replace('firebase_', '') : folderId;
    
    // Recupera il documento della cartella da Firestore
    const folderRef = doc(firestore, "folders", normalizedId);
    const folderSnap = await getDoc(folderRef);
    
    if (folderSnap.exists()) {
      const folderData = folderSnap.data();
      // Verifica che la cartella appartenga all'utente corrente
      if (folderData.userId === userId) {
        return {
          id: normalizedId,
          ...folderData
        };
      } else {
        console.error('La cartella non appartiene all\'utente corrente');
        return null;
      }
    } else {
      return null;
    }
  } catch (error) {
    console.error('Errore nel recupero della cartella:', error);
    return null;
  }
};

// Rimuove una sottocartella da una cartella principale
export const removeSubfolderFromFolder = async (parentFolderId, subfolderId) => {
  try {
    if (!parentFolderId || !subfolderId) {
      console.error('ID mancanti per la rimozione della sottocartella');
      return false;
    }
    
    // Normalizza l'ID della cartella principale per uniformità
    const normalizedParentId = parentFolderId.startsWith('firebase_') ? 
      parentFolderId.replace('firebase_', '') : parentFolderId;
    
    // Ottieni la cartella principale
    const parentFolder = await getFolderById(normalizedParentId);
    if (!parentFolder) {
      console.error(`Cartella principale ${normalizedParentId} non trovata`);
      return false;
    }
    
    // Verifica che la cartella abbia sottocartelle
    if (!parentFolder.subfolders || !Array.isArray(parentFolder.subfolders) || parentFolder.subfolders.length === 0) {
      return false; 
    }
    
    // Verifica che la sottocartella esista
    const subfolder = parentFolder.subfolders.find(sf => sf.id === subfolderId);
    if (!subfolder) {
      console.error(`Sottocartella ${subfolderId} non trovata nella cartella ${normalizedParentId}`);
      return false;
    }
    
    // Filtra la sottocartella da rimuovere
    const updatedSubfolders = parentFolder.subfolders.filter(sf => sf.id !== subfolderId);

    // Aggiorna la cartella principale senza la sottocartella
    return updateFirestoreFolder(normalizedParentId, {
      subfolders: updatedSubfolders
    });
  } catch (error) {
    console.error('Errore nella rimozione della sottocartella:', error);
    return false;
  }
};

// ============ MATURITA EXAM PERSISTENCE ============

/**
 * Save a completed maturita exam to the "exam" collection.
 * @param {string} userId
 * @param {Object} examData - { indirizzo, materie, examMode, overallScore, rubrica, perMateria, strengths, weaknesses, advice, studyPlan }
 * @returns {string} The generated exam document ID
 */
export const saveMaturitaExam = async (userId, examData) => {
  const examRef = await addDoc(collection(firestore, "exam"), {
    userID: userId,
    status: "completed",
    reviewMode: true,
    examEnd: serverTimestamp(),
    totalQuestions: (examData.materie?.length || 4) * 4,
    questionsAnswered: (examData.materie?.length || 4) * 4,
    isMaturita: true,
    indirizzo: examData.indirizzo || '',
    materie: examData.materie || [],
    examMode: examData.examMode || 'interrogazione',
    overallScore: examData.overallScore ?? 0,
    rubrica: examData.rubrica || {},
    perMateria: examData.perMateria || {},
    strengths: examData.strengths || [],
    weaknesses: examData.weaknesses || [],
    advice: examData.advice || '',
    studyPlan: examData.studyPlan || '',
    ...(examData.type && { type: examData.type }),
  });
  return examRef.id;
};

/**
 * Save maturita exam Q&A to the "examSessions" collection.
 * Uses the same q0/q1/... structure as saveQAExam for consistency.
 * @param {string} examId - The exam document ID from saveMaturitaExam
 * @param {Array} allQA - Array of { question, answer, correct_answer, materia }
 */
export const saveMaturitaExamQA = async (examId, allQA) => {
  const chatMessages = (allQA || []).map((qa, index) => ({
    [`q${index}`]: {
      question: qa.question || '',
      userAnswer: qa.answer || '',
      correctAnswer: qa.correct_answer || '',
      materia: qa.materia || '',
      score: null,
    },
  }));

  const sessionOwner = getAuth().currentUser?.uid;
  await setDoc(doc(firestore, "examSessions", examId), {
    chatMessages,
    reviewMode: true,
    isMaturita: true,
    lastUpdated: serverTimestamp(),
    // STU-480: proprietario esplicito, come l'esame appena salvato.
    ...(sessionOwner ? { userID: sessionOwner } : {}),
  });
};

/**
 * Get completed maturita exams for a user, with cursor-based pagination.
 * @param {string} userId
 * @param {Object} options - { limitCount, startAfterDoc }
 * @returns {{ exams: Array, lastDoc: Object|null, hasMore: boolean }}
 */
export const getMaturitaCompletedExams = async (userId, { limitCount = 5, startAfterDoc = null } = {}) => {
  const constraints = [
    where("userID", "==", userId),
    where("isMaturita", "==", true),
    where("status", "==", "completed"),
    orderBy("examEnd", "desc"),
    limit(limitCount),
  ];

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  const examsSnapshot = await getDocs(
    query(collection(firestore, "exam"), ...constraints)
  );

  if (examsSnapshot.empty) {
    return { exams: [], lastDoc: null, hasMore: false };
  }

  const examDocs = examsSnapshot.docs;
  const exams = examDocs.map((d) => ({ examID: d.id, ...d.data() }));
  const lastDoc = examDocs[examDocs.length - 1];

  return {
    exams,
    lastDoc,
    hasMore: examDocs.length === limitCount,
  };
};

/**
 * Get a single maturita exam by its ID.
 * @param {string} examId
 * @returns {Object|null}
 */
export const getMaturitaExamById = async (examId) => {
  const examSnap = await getDoc(doc(firestore, "exam", examId));
  if (!examSnap.exists()) return null;
  return { examID: examSnap.id, ...examSnap.data() };
};
